<template>
  <div class="tac-lookup-shell py-8 px-4">
    <v-container class="dashboard-container" fluid>
      <!-- Header Section -->
      <div class="dashboard-header d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-6">
        <div class="header-copy">
          <div class="eyebrow mb-2">Operations · Studio Software · Legacy Database</div>
          <div class="d-flex align-center flex-wrap">
            <h1 class="text-h4 font-weight-black white--text italic-title mr-3">TAC DATEN-EXPLORER</h1>
            <v-chip small color="#fbc02d" class="black--text font-weight-black" label> {{ tables.length }} Tabellen </v-chip>
          </div>
          <h2 class="subtitle-1 grey--text text--lighten-1 font-weight-medium mt-1">
            Rohdaten durchsuchen, Beziehungen nachvollziehen und Mitglieder vollständig verstehen.
          </h2>
        </div>
        <div class="d-flex flex-wrap gap-3 mt-4 mt-sm-0">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="#fbc02d"
                aria-label="Kassenbeispiele exportieren"
                :loading="posBookingExamplesDownloading"
                :disabled="posBookingExamplesDownloading || posMasterDataDownloading || posSchemaDownloading"
                v-bind="attrs"
                v-on="on"
                @click="downloadPosBookingExamples">
                <v-icon>mdi-file-document-outline</v-icon>
              </v-btn>
            </template>
            <span>Kassenbeispiele exportieren</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="#fbc02d"
                aria-label="Kassenstammdaten exportieren"
                :loading="posMasterDataDownloading"
                :disabled="posMasterDataDownloading || posSchemaDownloading || posBookingExamplesDownloading"
                v-bind="attrs"
                v-on="on"
                @click="downloadPosMasterData">
                <v-icon>mdi-file-table-outline</v-icon>
              </v-btn>
            </template>
            <span>Kassenstammdaten exportieren</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="#fbc02d"
                aria-label="Kassenstruktur exportieren"
                :loading="posSchemaDownloading"
                :disabled="posSchemaDownloading || posMasterDataDownloading || posBookingExamplesDownloading"
                v-bind="attrs"
                v-on="on"
                @click="downloadPosSchema">
                <v-icon>mdi-database-export-outline</v-icon>
              </v-btn>
            </template>
            <span>Kassenstruktur exportieren</span>
          </v-tooltip>
          <v-btn color="#fbc02d" class="black--text font-weight-bold" @click="openSchemaGuide">
            <v-icon left>mdi-book-open-outline</v-icon> Schema-Guide
          </v-btn>
          <v-btn text outlined color="grey lighten-1" @click="refreshData" :loading="loading">
            <v-icon left>mdi-refresh</v-icon> Aktualisieren
          </v-btn>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <v-tabs v-model="activeDashboardTab" background-color="transparent" color="#fbc02d" class="mb-6 rounded-lg custom-top-tabs">
        <v-tab class="font-weight-bold">
          <v-icon left class="mr-1">mdi-database-search</v-icon>
          Tabellen-Explorer
        </v-tab>
        <v-tab class="font-weight-bold">
          <v-icon left class="mr-1">mdi-vector-link</v-icon>
          Verknüpfter Lookup
        </v-tab>
      </v-tabs>

      <!-- Error Alert -->
      <v-alert v-if="errorAlert" type="error" dismissible class="mb-6 rounded-lg" @input="errorAlert = null">
        {{ errorAlert }}
      </v-alert>

      <v-tabs-items v-model="activeDashboardTab" style="background-color: transparent">
        <!-- Tab 1: Table Explorer -->
        <v-tab-item>
          <!-- Control Panel Card -->
          <v-card dark class="control-card pa-5 rounded-xl mb-6">
            <v-row class="align-center" dense>
              <!-- Table Selection Dropdown -->
              <v-col cols="12" md="6" lg="4">
                <div class="caption grey--text mb-1 text-uppercase font-weight-bold">Tabelle oder View</div>
                <v-select
                  v-model="selectedTable"
                  :items="tables"
                  label="Datenbanktabelle wählen..."
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-database-outline"
                  class="custom-select rounded-lg"
                  :loading="tablesLoading"></v-select>
              </v-col>

              <!-- Free Text Search Field -->
              <v-col cols="12" md="6" lg="5">
                <div class="caption grey--text mb-1 text-uppercase font-weight-bold">Volltextsuche</div>
                <v-text-field
                  v-model="searchQuery"
                  label="In Textspalten suchen..."
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  class="custom-search rounded-lg"
                  placeholder="Suchbegriff eingeben..."
                  @keydown.enter="fetchTableData"></v-text-field>
              </v-col>

              <!-- Selected Schema Stats -->
              <v-col cols="12" lg="3" class="text-lg-right pt-2 pt-lg-0">
                <div class="caption grey--text text-uppercase font-weight-bold">Tabelleninfo</div>
                <div class="white--text font-weight-bold text-h6">
                  {{ totalRows }} <span class="subtitle-2 grey--text font-weight-regular">Datensätze</span>
                </div>
              </v-col>
            </v-row>
            <div class="table-context mt-4 d-flex align-start">
              <v-icon small color="#fbc02d" class="mr-2 mt-1">mdi-information-outline</v-icon>
              <div>
                <span class="font-weight-bold white--text mr-2">{{ selectedTable }}</span>
                <span class="caption grey--text text--lighten-1">{{ getTableDescription(selectedTable) }}</span>
              </div>
            </div>
          </v-card>

          <!-- Main Data Table Card -->
          <v-card dark class="table-card rounded-xl pa-4">
            <v-data-table
              :headers="headers"
              :items="rows"
              :options.sync="options"
              :server-items-length="totalRows"
              :loading="loading"
              class="custom-table"
              loading-text="Querying database... Please wait"
              no-data-text="No records found matching the search criteria"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 100],
                'show-current-page': true,
                'show-first-last-page': true,
              }">
              <!-- Custom Header Styling -->
              <template v-slot:header.name="{ header }">
                <span class="text-uppercase font-weight-bold">{{ header.text }}</span>
              </template>

              <!-- Custom Dynamic Row Render with tooltips & click handlers -->
              <template v-slot:item="{ item }">
                <tr @click="handleRowClick(item)" :class="{ 'clickable-row': hasCustomerId(item) }" title="Click to view full customer history">
                  <td v-for="col in columns" :key="col.name" class="text-truncate" style="max-width: 250px">
                    <!-- Value truncation with tooltip if long -->
                    <v-tooltip bottom v-if="shouldShowTooltip(item[col.name])" color="#1c1c1e">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="truncated-text">
                          {{ item[col.name] }}
                        </span>
                      </template>
                      <span class="tooltip-content">{{ item[col.name] }}</span>
                    </v-tooltip>

                    <!-- Specific CustomerID Badge if column is ID -->
                    <span v-else-if="col.name.toUpperCase() === 'CUSTOMERID' && item[col.name]">
                      <v-chip x-small color="#fbc02d" class="black--text font-weight-bold px-2">
                        <v-icon x-small left class="black--text mr-1">mdi-account-circle</v-icon>
                        {{ item[col.name] }}
                      </v-chip>
                    </span>

                    <!-- Normal formatting -->
                    <span v-else>
                      {{ formatValue(item[col.name], col.type) }}
                    </span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <!-- Tab 2: Deep Data Mapper (Table-Only Lookup) -->
        <v-tab-item>
          <v-row>
            <!-- Left panel: Search & selector -->
            <v-col cols="12" md="4" lg="3">
              <v-card dark class="control-card pa-5 rounded-xl mb-6 lookup-search-card">
                <h3 class="subtitle-2 yellow--text text--darken-2 font-weight-bold text-uppercase mb-4 d-flex align-center">
                  <v-icon color="#fbc02d" class="mr-2">mdi-magnify-scan</v-icon> Entität suchen
                </h3>

                <!-- Entity Type Selection -->
                <div class="caption grey--text mb-2 text-uppercase font-weight-bold">Bereich</div>
                <v-btn-toggle
                  v-model="lookup.entityType"
                  mandatory
                  background-color="rgba(255, 255, 255, 0.03)"
                  color="#fbc02d"
                  class="custom-btn-toggle mb-4 d-flex"
                  @change="onLookupTypeChange">
                  <v-btn value="customer" class="flex-grow-1 font-weight-bold rounded-lg text-caption">
                    <v-icon x-small left class="mr-1">mdi-account</v-icon> Mitglied
                  </v-btn>
                  <v-btn value="location" class="flex-grow-1 font-weight-bold rounded-lg text-caption">
                    <v-icon x-small left class="mr-1">mdi-map-marker</v-icon> Standort
                  </v-btn>
                  <v-btn value="ressortgroup" class="flex-grow-1 font-weight-bold rounded-lg text-caption">
                    <v-icon x-small left class="mr-1">mdi-office-building</v-icon> Studio
                  </v-btn>
                </v-btn-toggle>

                <!-- Search Input -->
                <div class="caption grey--text mb-1 text-uppercase font-weight-bold">Name, ID, E-Mail oder Telefon</div>
                <v-text-field
                  v-model="lookup.searchQuery"
                  label="Suchen..."
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  class="custom-search rounded-lg mb-2"
                  placeholder="Suchbegriff..."
                  @input="debounceLookupSearch"
                  :loading="lookup.searchLoading"></v-text-field>

                <!-- Autocomplete suggestions -->
                <div v-if="lookup.searchResults.length > 0" class="results-list-container rounded-lg pa-1 mt-2">
                  <v-list dense bg-color="transparent" class="pa-0">
                    <v-list-item
                      v-for="item in lookup.searchResults"
                      :key="item.type + '-' + item.id"
                      class="rounded-lg mb-1 lookup-result-item"
                      @click="selectLookupEntity(item)"
                      :class="{
                        'active-result-item':
                          lookup.selectedEntity && lookup.selectedEntity.id === item.id && lookup.selectedEntity.type === item.type,
                      }">
                      <v-list-item-avatar
                        size="32"
                        :color="item.type === 'customer' ? '#fbc02d' : item.type === 'location' ? 'blue' : 'green'"
                        class="black--text font-weight-bold">
                        <v-icon x-small color="black">
                          {{ item.type === "customer" ? "mdi-account" : item.type === "location" ? "mdi-map-marker" : "mdi-office-building" }}
                        </v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold text-body-2 white--text">
                          {{ item.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="grey--text caption text-truncate">
                          {{ item.subtitle }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <div v-else-if="lookup.searchQuery && !lookup.searchLoading" class="caption grey--text text-center py-4 italic">
                  Keine passenden Einträge gefunden.
                </div>
                <div v-else class="caption grey--text text-center py-4 italic">Suchbegriff eingeben oder die neuesten Einträge anzeigen.</div>
              </v-card>

              <!-- Selected Entity profile info -->
              <v-card v-if="lookup.selectedEntity && lookup.data" dark class="control-card pa-4 rounded-xl entity-profile-card">
                <h3 class="subtitle-2 yellow--text text--darken-2 font-weight-bold text-uppercase mb-3 d-flex align-center">
                  <v-icon color="#fbc02d" class="mr-2">mdi-card-account-details-outline</v-icon> Stammdaten
                </h3>
                <div class="d-flex align-center mb-3">
                  <v-avatar
                    :color="lookup.selectedEntity.type === 'customer' ? '#fbc02d' : lookup.selectedEntity.type === 'location' ? 'blue' : 'green'"
                    size="38"
                    class="black--text font-weight-bold mr-2">
                    <v-icon size="18" color="black">
                      {{
                        lookup.selectedEntity.type === "customer"
                          ? "mdi-account"
                          : lookup.selectedEntity.type === "location"
                          ? "mdi-map-marker"
                          : "mdi-office-building"
                      }}
                    </v-icon>
                  </v-avatar>
                  <div>
                    <h4 class="text-body-2 font-weight-bold white--text text-truncate" style="max-width: 170px">
                      {{ lookup.selectedEntity.title }}
                    </h4>
                    <span class="caption grey--text text-uppercase font-weight-bold"> ID: {{ lookup.selectedEntity.id }} </span>
                  </div>
                </div>

                <v-divider class="grey darken-3 mb-3"></v-divider>

                <!-- Profile details loop -->
                <div v-for="field in lookupProfileFields" :key="field.label" class="py-1">
                  <div class="caption grey--text text-uppercase font-weight-medium" style="font-size: 10px; line-height: 1.1">{{ field.label }}</div>
                  <div class="body-2 white--text font-weight-bold" style="word-break: break-word; line-height: 1.3">{{ field.value || "-" }}</div>
                </div>
              </v-card>
            </v-col>

            <!-- Right panel: Mapped data tables -->
            <v-col cols="12" md="8" lg="9">
              <div v-if="lookup.loading" class="d-flex flex-column align-center justify-center py-12 fill-height">
                <v-progress-circular indeterminate color="#fbc02d" size="56" width="5" class="mb-3"></v-progress-circular>
                <div class="subtitle-2 grey--text">Mapping relations from database tables...</div>
              </div>

              <div v-else-if="lookup.selectedEntity && lookup.data">
                <!-- Render Mapped Tab Navigation -->
                <v-tabs
                  v-model="lookup.activeTab"
                  background-color="transparent"
                  color="#fbc02d"
                  show-arrows
                  class="custom-tabs lookup-tabs rounded-lg mb-4">
                  <!-- Customer Sub-Tabs -->
                  <template v-if="lookup.selectedEntity.type === 'customer'">
                    <v-tab class="font-weight-bold text-caption"><v-icon small left>mdi-view-dashboard-outline</v-icon>Übersicht</v-tab>
                    <v-tab class="font-weight-bold text-caption"
                      ><v-icon small left>mdi-file-sign</v-icon>Verträge ({{ lookup.data.contracts.length }})</v-tab
                    >
                    <v-tab class="font-weight-bold text-caption"
                      ><v-icon small left>mdi-door-open</v-icon>Zutritt ({{ (lookup.data.accessPasses || []).length }})</v-tab
                    >
                    <v-tab class="font-weight-bold text-caption">Konten ({{ lookup.data.accounts.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption">Transaktionen ({{ lookup.data.transactions.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption">Rechnungen ({{ lookup.data.bills.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption">Besuche ({{ lookup.data.checkins.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption">Termine ({{ customerActivityCount }})</v-tab>
                    <v-tab class="font-weight-bold text-caption"
                      ><v-icon small left>mdi-account-heart-outline</v-icon>CRM ({{ customerCrmCount }})</v-tab
                    >
                    <v-tab class="font-weight-bold text-caption">Historie ({{ lookup.data.historyData.length }})</v-tab>
                  </template>

                  <!-- Location Sub-Tabs -->
                  <template v-else-if="lookup.selectedEntity.type === 'location'">
                    <v-tab class="font-weight-bold text-caption px-2">Resorts ({{ lookup.data.resorts.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption px-2"
                      >Configs ({{ lookup.data.accessConfigSurcharges.length + lookup.data.accessDeviceUsageAdditional.length }})</v-tab
                    >
                    <v-tab class="font-weight-bold text-caption px-2">Checkins ({{ lookup.data.checkins.length }})</v-tab>
                  </template>

                  <!-- Resort Group Sub-Tabs -->
                  <template v-else-if="lookup.selectedEntity.type === 'ressortgroup'">
                    <v-tab class="font-weight-bold text-caption px-1">Abo-Strukturen ({{ displayedContractCategories.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption px-1">Resorts ({{ lookup.data.resorts.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption px-1">Locations ({{ lookup.data.locations.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption px-1">Contracts ({{ lookup.data.contracts.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption px-1">Bills ({{ lookup.data.bills.length }})</v-tab>
                    <v-tab class="font-weight-bold text-caption px-1">Checkins ({{ lookup.data.checkins.length }})</v-tab>
                  </template>
                </v-tabs>

                <!-- Sub Tabs Items Content -->
                <v-tabs-items v-model="lookup.activeTab" style="background-color: transparent">
                  <!-- CUSTOMER DETAILS RENDER -->
                  <template v-if="lookup.selectedEntity.type === 'customer'">
                    <!-- 1. Relationship overview -->
                    <v-tab-item>
                      <div class="customer-overview">
                        <v-alert v-if="(lookup.data.warnings || []).length" dense outlined type="warning" class="rounded-lg mb-4">
                          Einzelne optionale Module konnten auf dieser Datenbankversion nicht geladen werden:
                          {{ lookupWarningSections }}. Die übrigen Bereiche sind weiterhin verfügbar.
                        </v-alert>
                        <v-row dense class="mb-2">
                          <v-col v-for="stat in customerOverviewStats" :key="stat.label" cols="12" sm="6" lg="3">
                            <v-card dark class="overview-stat-card pa-4 rounded-xl fill-height">
                              <div class="d-flex align-start justify-space-between">
                                <div>
                                  <div class="overview-stat-label">{{ stat.label }}</div>
                                  <div class="text-h5 font-weight-black white--text mt-1">{{ stat.value }}</div>
                                  <div class="caption grey--text text--lighten-1 mt-1">{{ stat.hint }}</div>
                                </div>
                                <v-avatar size="40" :color="stat.color" class="overview-stat-icon">
                                  <v-icon size="20" color="black">{{ stat.icon }}</v-icon>
                                </v-avatar>
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" lg="7">
                            <v-card dark class="table-card pa-5 rounded-xl fill-height">
                              <div class="section-heading mb-4">
                                <div>
                                  <div class="eyebrow">Aktueller Stand</div>
                                  <h4 class="subtitle-1 font-weight-bold white--text">Mitglied auf einen Blick</h4>
                                </div>
                                <v-chip small :color="customerHealth.color" class="black--text font-weight-bold" label>
                                  {{ customerHealth.label }}
                                </v-chip>
                              </div>

                              <div class="overview-list">
                                <div class="overview-list-item">
                                  <v-icon color="#fbc02d" class="mr-3">mdi-file-sign</v-icon>
                                  <div class="flex-grow-1">
                                    <div class="font-weight-bold white--text">Vertragsstatus</div>
                                    <div class="caption grey--text">
                                      {{ activeCustomerContracts.length }} aktiv / nutzbar, {{ lookup.data.contracts.length }} insgesamt
                                    </div>
                                  </div>
                                  <span class="font-weight-bold">{{ nextContractEnd }}</span>
                                </div>
                                <div class="overview-list-item">
                                  <v-icon color="#64d98b" class="mr-3">mdi-door-open</v-icon>
                                  <div class="flex-grow-1">
                                    <div class="font-weight-bold white--text">Letzter Besuch</div>
                                    <div class="caption grey--text">{{ latestCheckinLocation }}</div>
                                  </div>
                                  <span class="font-weight-bold">{{ latestCheckinDate }}</span>
                                </div>
                                <div class="overview-list-item">
                                  <v-icon color="#7bb8ff" class="mr-3">mdi-calendar-clock</v-icon>
                                  <div class="flex-grow-1">
                                    <div class="font-weight-bold white--text">Nächster Termin</div>
                                    <div class="caption grey--text">{{ nextBookingName }}</div>
                                  </div>
                                  <span class="font-weight-bold">{{ nextBookingDate }}</span>
                                </div>
                                <div class="overview-list-item">
                                  <v-icon color="#d7a8ff" class="mr-3">mdi-account-details-outline</v-icon>
                                  <div class="flex-grow-1">
                                    <div class="font-weight-bold white--text">Profil-Verknüpfungen</div>
                                    <div class="caption grey--text">Adressen, Kontakte, Segmente, Herkunft und CRM</div>
                                  </div>
                                  <span class="font-weight-bold">{{ customerCrmCount }}</span>
                                </div>
                              </div>
                            </v-card>
                          </v-col>

                          <v-col cols="12" lg="5">
                            <v-card dark class="table-card pa-5 rounded-xl fill-height">
                              <div class="eyebrow">Nachvollziehbarkeit</div>
                              <h4 class="subtitle-1 font-weight-bold white--text mb-1">Verknüpfte Datenquellen</h4>
                              <p class="caption grey--text text--lighten-1 mb-4">
                                Die Ansicht folgt dem Profil- und MemberDetail-Modell der Hersteller-API und zeigt den verwendeten Join-Pfad.
                              </p>
                              <div class="source-map-list">
                                <div v-for="source in lookup.data.relationSources || []" :key="source.section" class="source-map-item">
                                  <div class="d-flex align-center justify-space-between mb-1">
                                    <span class="font-weight-bold white--text">{{ source.section }}</span>
                                    <v-icon x-small color="#64d98b">mdi-check-decagram</v-icon>
                                  </div>
                                  <div class="caption yellow--text text--darken-2 source-table-name">{{ source.table }}</div>
                                  <div class="caption grey--text">{{ source.relation }}</div>
                                </div>
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-tab-item>

                    <!-- 2. Contracts -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">M_T_CONTRACTS</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.contracts, 'customer_contracts')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th style="width: 40px"></th>
                              <th>Contract ID</th>
                              <th>Abo Name (ID)</th>
                              <th>Resort Group ID</th>
                              <th>State</th>
                              <th>Start Date</th>
                              <th>End Date</th>
                              <th>Terminated Date</th>
                              <th>Deleted</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="c in lookup.data.contracts">
                              <!-- Main row -->
                              <tr :key="'row-' + c.CONTRACTID" @click="toggleContractExpand(c.CONTRACTID)" class="clickable-row">
                                <td>
                                  <v-icon small color="#fbc02d">
                                    {{ lookup.expandedContracts[c.CONTRACTID] ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                  </v-icon>
                                </td>
                                <td class="font-weight-bold yellow--text text--lighten-2">{{ c.CONTRACTID }}</td>
                                <td class="font-weight-bold white--text">
                                  {{ c.CATEGORY_NAME || "Standard Abo" }}
                                  <span class="caption grey--text font-weight-regular">({{ c.CATEGORYID }})</span>
                                </td>
                                <td>{{ c.RESSORTGROUPID }}</td>
                                <td>
                                  <v-chip x-small :color="getContractStateColor(c.CONTRACTSTATE)" class="black--text font-weight-bold" label>
                                    {{ getContractStateText(c.CONTRACTSTATE) }}
                                  </v-chip>
                                </td>
                                <td>{{ formatDateOnly(c.STARTDATE) }}</td>
                                <td>{{ formatDateOnly(c.ENDDATE) }}</td>
                                <td>{{ formatDateOnly(c.TERMINATE_DATE) }}</td>
                                <td>
                                  <v-chip x-small :color="c.DELETED ? 'red accent-2' : 'green accent-3'" class="black--text font-weight-bold">
                                    {{ c.DELETED ? "Yes" : "No" }}
                                  </v-chip>
                                </td>
                              </tr>
                              <!-- Details expand row -->
                              <tr
                                v-if="lookup.expandedContracts[c.CONTRACTID]"
                                :key="'details-' + c.CONTRACTID"
                                style="background-color: rgba(255, 255, 255, 0.015)">
                                <td colspan="9" class="pa-4">
                                  <v-card
                                    flat
                                    dark
                                    class="pa-4 rounded-lg"
                                    style="background-color: rgba(0, 0, 0, 0.2); border: 1px solid rgba(255, 255, 255, 0.05) !important">
                                    <v-row dense>
                                      <v-col cols="12" sm="6" md="4" v-for="(val, key) in getFilteredContractDetails(c)" :key="key" class="py-1">
                                        <div class="caption grey--text text-uppercase font-weight-medium" style="font-size: 10px; line-height: 1.2">
                                          {{ key }}
                                        </div>
                                        <div class="body-2 white--text font-weight-bold text-truncate" :title="val">{{ val }}</div>
                                      </v-col>
                                    </v-row>
                                  </v-card>
                                </td>
                              </tr>
                            </template>
                            <tr v-if="lookup.data.contracts.length === 0">
                              <td colspan="9" class="text-center py-6 grey--text">No contracts registered in M_T_CONTRACTS.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 3. Access passes, media and access decisions -->
                    <v-tab-item>
                      <div class="d-flex flex-column gap-4">
                        <v-card dark class="pa-4 rounded-xl table-card">
                          <div class="section-heading mb-3">
                            <div>
                              <div class="eyebrow">MemberDetail · AccessPassDetail</div>
                              <h4 class="subtitle-2 font-weight-bold white--text mb-0">Zutrittspässe</h4>
                            </div>
                            <v-btn
                              x-small
                              color="#fbc02d"
                              class="black--text font-weight-bold"
                              @click="exportLookupTableToCSV(lookup.data.accessPasses || [], 'customer_access_passes')">
                              <v-icon left x-small>mdi-download</v-icon> CSV
                            </v-btn>
                          </div>
                          <div class="table-scroll">
                            <v-simple-table dark class="detail-table">
                              <thead>
                                <tr>
                                  <th>Pass / Detail</th>
                                  <th>Vorlage</th>
                                  <th>Gültigkeit</th>
                                  <th>Kontingent</th>
                                  <th>Preis</th>
                                  <th>Gültigkeitsstatus</th>
                                  <th>Sperre</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr
                                  v-for="pass in lookup.data.accessPasses || []"
                                  :key="pass.ACCESSPASSID + '-' + (pass.ACCESSPASSDETAILID || 'base')">
                                  <td>
                                    <div class="font-weight-bold yellow--text text--lighten-2">{{ pass.ACCESSPASSID }}</div>
                                    <div class="caption grey--text">Detail {{ pass.ACCESSPASSDETAILID || "-" }}</div>
                                  </td>
                                  <td>
                                    <div class="font-weight-bold white--text">{{ pass.TEMPLATE_NAME || "Ohne Vorlage" }}</div>
                                    <div class="caption grey--text">{{ pass.TEMPLATE_MAPPING || pass.ACCESSPASS_MAPPING || "-" }}</div>
                                  </td>
                                  <td>
                                    <div>{{ formatDateOnly(pass.VALID_FROM) }}</div>
                                    <div class="caption grey--text">bis {{ formatDateOnly(pass.VALID_UNTIL) }}</div>
                                  </td>
                                  <td>
                                    {{ pass.QUANTITY !== null && pass.QUANTITY !== undefined ? pass.QUANTITY : "-"
                                    }}<span v-if="pass.BONUSQUANTITY" class="green--text text--lighten-2"> +{{ pass.BONUSQUANTITY }}</span>
                                  </td>
                                  <td>CHF {{ formatMoney(pass.FINALPRICE !== null ? pass.FINALPRICE : pass.BASEPRICE) }}</td>
                                  <td>
                                    <v-chip x-small :color="getAccessPassValidity(pass).color" class="black--text font-weight-bold" label>
                                      {{ getAccessPassValidity(pass).label }}
                                    </v-chip>
                                  </td>
                                  <td>
                                    <span v-if="pass.LOCKREASONTEXT" class="red--text text--lighten-2">{{ pass.LOCKREASONTEXT }}</span>
                                    <span v-else class="grey--text">Keine</span>
                                  </td>
                                </tr>
                                <tr v-if="!(lookup.data.accessPasses || []).length">
                                  <td colspan="7" class="text-center py-6 grey--text">Keine Zutrittspässe gefunden.</td>
                                </tr>
                              </tbody>
                            </v-simple-table>
                          </div>
                        </v-card>

                        <v-row>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Medien / Transponder</h4>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.memberMedia || []).length }}</v-chip>
                              </div>
                              <div v-for="medium in lookup.data.memberMedia || []" :key="medium.ContractCustomerMediumID" class="compact-record">
                                <v-icon color="#fbc02d" class="mr-3">mdi-card-bulleted-outline</v-icon>
                                <div class="flex-grow-1 min-width-0">
                                  <div class="font-weight-bold white--text text-truncate">{{ medium.Name || "Mitgliedermedium" }}</div>
                                  <div class="caption grey--text text-truncate">
                                    {{ maskMediumId(medium.MediumID) }} · Vertrag {{ medium.CONTRACTID }}
                                  </div>
                                </div>
                                <v-chip x-small outlined color="grey lighten-1">Typ {{ medium.MediumType }}</v-chip>
                              </div>
                              <div v-if="!(lookup.data.memberMedia || []).length" class="empty-compact">Keine Medien verknüpft.</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Prepaid-Karten</h4>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.prepaidCards || []).length }}</v-chip>
                              </div>
                              <div v-for="card in lookup.data.prepaidCards || []" :key="card.PrepaidCardID" class="compact-record">
                                <v-icon color="#7bb8ff" class="mr-3">mdi-credit-card-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">{{ card.NAME || card.Number || "Karte " + card.PrepaidCardID }}</div>
                                  <div class="caption grey--text">Erstellt {{ formatDateOnly(card.CreationDate) }} · State {{ card.State }}</div>
                                </div>
                                <span v-if="card.LockReasonText" class="caption red--text text--lighten-2">{{ card.LockReasonText }}</span>
                              </div>
                              <div v-if="!(lookup.data.prepaidCards || []).length" class="empty-compact">Keine Prepaid-Karten verknüpft.</div>
                            </v-card>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Abgewiesene Zutritte</h4>
                                <v-chip x-small :color="(lookup.data.denials || []).length ? 'red darken-2' : 'green darken-2'">{{
                                  (lookup.data.denials || []).length
                                }}</v-chip>
                              </div>
                              <div v-for="denial in (lookup.data.denials || []).slice(0, 10)" :key="denial.DenialID" class="compact-record">
                                <v-icon color="#ff7d87" class="mr-3">mdi-shield-alert-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">
                                    {{ denial.LOCATION_NAME || denial.LOCATION_SHORTNAME || "Standort " + denial.LocationID }}
                                  </div>
                                  <div class="caption grey--text">
                                    {{ formatDate(denial.DenialDate) }} · Gerät {{ denial.ACCESS_DEVICE_NAME || denial.ACCESSDEVICEID || "-" }}
                                  </div>
                                </div>
                                <v-chip x-small outlined color="red lighten-2">Typ {{ denial.DenialType }}</v-chip>
                              </div>
                              <div v-if="!(lookup.data.denials || []).length" class="empty-compact">Keine abgewiesenen Zutritte.</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Ruhezeiten / Sistierungen</h4>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.idlePeriods || []).length }}</v-chip>
                              </div>
                              <div
                                v-for="period in (lookup.data.idlePeriods || []).slice(0, 10)"
                                :key="period.CONTRACT_IDLEPERIODID"
                                class="compact-record">
                                <v-icon color="#d7a8ff" class="mr-3">mdi-pause-circle-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">{{ period.CATEGORY_NAME || "Vertrag " + period.CONTRACTID }}</div>
                                  <div class="caption grey--text">{{ formatDateOnly(period.IDLESTART) }} – {{ formatDateOnly(period.IDLEEND) }}</div>
                                </div>
                                <span class="caption grey--text">{{ period.REASON || period.IDLECOMMENT || "-" }}</span>
                              </div>
                              <div v-if="!(lookup.data.idlePeriods || []).length" class="empty-compact">Keine Ruhezeiten erfasst.</div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-tab-item>

                    <!-- 4. Accounts -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">M_T_ACCOUNTS</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.accounts, 'customer_accounts')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Account ID</th>
                              <th>Account Type ID</th>
                              <th>Resort Group ID</th>
                              <th>Balance</th>
                              <th>First Unpaid Booking</th>
                              <th>Due Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="a in lookup.data.accounts" :key="a.ACCOUNTID">
                              <td class="font-weight-bold">{{ a.ACCOUNTID }}</td>
                              <td>{{ a.ACCOUNTTYPEID }}</td>
                              <td>{{ a.RESSORTGROUPID }}</td>
                              <td class="font-weight-bold" :class="a.BALANCE >= 0 ? 'green--text text--accent-3' : 'red--text text--accent-2'">
                                CHF {{ parseFloat(a.BALANCE || 0).toFixed(2) }}
                              </td>
                              <td>{{ formatDate(a.FIRSTUNPAIDTRANSACTION) }}</td>
                              <td>{{ formatDate(a.DUEDATE) }}</td>
                            </tr>
                            <tr v-if="lookup.data.accounts.length === 0">
                              <td colspan="6" class="text-center py-6 grey--text">No accounts registered in M_T_ACCOUNTS.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 3. Transactions -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">M_T_ACCOUNT_TRANSACTIONS</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.transactions, 'customer_transactions')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Transaction ID</th>
                              <th>Account ID</th>
                              <th>Date</th>
                              <th>Description</th>
                              <th>Value</th>
                              <th>Type</th>
                              <th>Source Resort Group</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="t in lookup.data.transactions" :key="t.ACCOUNT_TRANSACTIONID">
                              <td>{{ t.ACCOUNT_TRANSACTIONID }}</td>
                              <td>{{ t.ACCOUNTID }}</td>
                              <td>{{ formatDate(t.BILLDATE) }}</td>
                              <td class="text-truncate" style="max-width: 200px">{{ t.DESCRIPTION }}</td>
                              <td class="font-weight-black" :class="t.VALUE >= 0 ? 'green--text text--accent-3' : 'red--text text--accent-2'">
                                CHF {{ parseFloat(t.VALUE || 0).toFixed(2) }}
                              </td>
                              <td>{{ t.TYPE }}</td>
                              <td>{{ t.SOURCE_RESSORTGROUPID || "-" }}</td>
                            </tr>
                            <tr v-if="lookup.data.transactions.length === 0">
                              <td colspan="7" class="text-center py-6 grey--text">No transactions found in M_T_ACCOUNT_TRANSACTIONS.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 4. Bills -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">B_T_BILLS</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.bills, 'customer_bills')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Bill ID</th>
                              <th>Bill No</th>
                              <th>Bill Date</th>
                              <th>Kassa ID</th>
                              <th>Resort Group ID</th>
                              <th>Total Amount</th>
                              <th>Zahlungsart</th>
                              <th>Storno</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="b in lookup.data.bills" :key="b.BILLID">
                              <td class="font-weight-bold">{{ b.BILLID }}</td>
                              <td>{{ b.BILLNO || "-" }}</td>
                              <td>{{ formatDate(b.BILLDATE) }}</td>
                              <td>{{ b.KASSAID }}</td>
                              <td>{{ b.RESSORTGROUPID }}</td>
                              <td class="font-weight-bold white--text">CHF {{ parseFloat(b.TOTALAMOUNT || 0).toFixed(2) }}</td>
                              <td>
                                <div v-for="payment in getBillPayments(b.BILLID)" :key="payment.BILL_PAYMETHOD_ID" class="caption">
                                  <span class="white--text">{{ payment.PAYMETHOD_NAME || "Methode " + payment.PAYMETHODID }}</span>
                                  <span class="grey--text"> · CHF {{ formatMoney(payment.VALUE) }}</span>
                                </div>
                                <span v-if="getBillPayments(b.BILLID).length === 0" class="grey--text">-</span>
                              </td>
                              <td>
                                <v-chip x-small :color="b.STORNO ? 'red accent-2' : 'grey darken-3'" class="black--text font-weight-bold">
                                  {{ b.STORNO ? "Storno" : "No" }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.bills.length === 0">
                              <td colspan="8" class="text-center py-6 grey--text">Keine Rechnungen in B_T_BILLS gefunden.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 5. Checkins -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">CHECKINS (Combined M_A_CHECKINS_*)</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.checkins, 'customer_checkins')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Checkin ID</th>
                              <th>Checkin Date</th>
                              <th>Checkout Date</th>
                              <th>Location ID</th>
                              <th>Key Code</th>
                              <th>Source Table</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="c in lookup.data.checkins" :key="c.CHECKINID + '-' + c._sourceTable">
                              <td>{{ c.CHECKINID }}</td>
                              <td class="green--text text--lighten-2">{{ formatDate(c.CHECKINDATE) }}</td>
                              <td class="grey--text">{{ formatDate(c.CHECKOUTDATE) }}</td>
                              <td>{{ c.LOCATIONID }}</td>
                              <td>{{ c.KEY || "-" }}</td>
                              <td>
                                <v-chip x-small color="grey darken-3" outlined class="grey--text text--lighten-1">
                                  {{ c._sourceTable }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.checkins.length === 0">
                              <td colspan="6" class="text-center py-6 grey--text">No checkins registered in M_A_CHECKINS*.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 6. Bookings -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">R_T_BOOKINGS</h4>
                          <div class="d-flex align-center gap-2">
                            <v-btn
                              x-small
                              color="#fbc02d"
                              class="black--text font-weight-bold mr-2"
                              @click="exportLookupTableToCSV(lookup.data.bookings, 'customer_bookings')">
                              <v-icon left x-small>mdi-download</v-icon> Export CSV (Kunde)
                            </v-btn>
                            <v-btn
                              x-small
                              color="blue lighten-1"
                              class="black--text font-weight-bold"
                              :loading="lookup.allBookingsDownloading"
                              @click="downloadAllBookingsCSV">
                              <v-icon left x-small>mdi-download-multiple</v-icon> Alle Termine (CSV)
                            </v-btn>
                          </div>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Booking ID</th>
                              <th>Res. ID</th>
                              <th>Behandlung (Dauer)</th>
                              <th>Kategorie</th>
                              <th>Trainer / Therapeut</th>
                              <th>Raum</th>
                              <th>Studio</th>
                              <th>Preis</th>
                              <th>Datum / Uhrzeit</th>
                              <th>Erstellt von</th>
                              <th>Notiz</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="b in lookup.data.bookings" :key="b.BOOKINGID">
                              <td class="font-weight-bold">{{ b.BOOKINGID }}</td>
                              <td>{{ b.RESERVATIONID }}</td>
                              <td>
                                <div class="font-weight-bold yellow--text text--lighten-2 d-flex align-center">
                                  {{ b.TEMPLATE_NAME || "-" }}
                                  <v-tooltip bottom v-if="b.TEMPLATE_DESCRIPTION">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small color="grey lighten-1" class="ml-1" v-bind="attrs" v-on="on"> mdi-information-outline </v-icon>
                                    </template>
                                    <span style="max-width: 250px; display: inline-block">{{ b.TEMPLATE_DESCRIPTION }}</span>
                                  </v-tooltip>
                                </div>
                                <div class="text-caption grey--text" v-if="b.TEMPLATE_DURATION">{{ b.TEMPLATE_DURATION }} Min.</div>
                                <div class="text-caption blue--text text--lighten-3" v-if="b.RESERVATION_LABEL">Titel: {{ b.RESERVATION_LABEL }}</div>
                                <div class="text-caption orange--text text--lighten-3" v-if="b.COURSE_NAME">Kurs: {{ b.COURSE_NAME }}</div>
                              </td>
                              <td>{{ b.CATEGORY_NAME || "-" }}</td>
                              <td>{{ b.TRAINER_NAMES || "-" }}</td>
                              <td>{{ b.ROOM_NAMES || "-" }}</td>
                              <td>{{ b.RESORT_NAME || "-" }}</td>
                              <td>CHF {{ parseFloat(b.PRICE || 0).toFixed(2) }}</td>
                              <td>
                                <div>{{ formatDate(b.STARTDATE) }}</div>
                                <div class="text-caption grey--text">Erstellt: {{ formatDate(b.CREATION_TIME) }}</div>
                              </td>
                              <td>{{ b.CREATED_BY || "-" }}</td>
                              <td>
                                <div v-if="b.NOTE || b.RESERVATION_NOTE">
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <span v-bind="attrs" v-on="on" class="text-truncate d-inline-block" style="max-width: 150px">
                                        {{ b.NOTE || b.RESERVATION_NOTE }}
                                      </span>
                                    </template>
                                    <div>
                                      <div v-if="b.NOTE"><strong>Buchung:</strong> {{ b.NOTE }}</div>
                                      <div v-if="b.RESERVATION_NOTE" class="mt-1"><strong>Termin:</strong> {{ b.RESERVATION_NOTE }}</div>
                                    </div>
                                  </v-tooltip>
                                </div>
                                <span v-else>-</span>
                              </td>
                              <td>
                                <v-tooltip bottom v-if="b.STORNO">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-chip x-small color="red accent-2" class="black--text font-weight-bold" v-bind="attrs" v-on="on">
                                      Storno
                                    </v-chip>
                                  </template>
                                  <div>
                                    <strong>Storniert am:</strong> {{ formatDate(b.CANCEL_DATE) }}<br />
                                    <strong>Grund:</strong> {{ b.CANCELREASON || "-" }}
                                  </div>
                                </v-tooltip>
                                <v-chip x-small color="green darken-2" class="white--text font-weight-bold" v-else> Aktiv </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.bookings.length === 0">
                              <td colspan="12" class="text-center py-6 grey--text">No bookings found in R_T_BOOKINGS.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>

                      <v-row class="mt-1">
                        <v-col cols="12" lg="7">
                          <v-card dark class="pa-4 rounded-xl table-card fill-height">
                            <div class="section-heading mb-3">
                              <div>
                                <div class="eyebrow">R_T_PACKAGE_CUSTOMER → R_M_PACKAGE</div>
                                <h4 class="subtitle-2 font-weight-bold white--text">Pakete / Arrangements</h4>
                              </div>
                              <v-btn x-small outlined color="#fbc02d" @click="exportLookupTableToCSV(lookup.data.packages || [], 'customer_packages')"
                                >CSV</v-btn
                              >
                            </div>
                            <div class="table-scroll">
                              <v-simple-table dark class="detail-table">
                                <thead>
                                  <tr>
                                    <th>ID</th>
                                    <th>Paket</th>
                                    <th>Zeitraum</th>
                                    <th>Studio</th>
                                    <th>Bezahlt</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="pkg in lookup.data.packages || []" :key="pkg.PACKAGE_CUSTOMERID">
                                    <td class="yellow--text text--lighten-2 font-weight-bold">{{ pkg.PACKAGE_CUSTOMERID }}</td>
                                    <td>
                                      <div class="font-weight-bold white--text">{{ pkg.PACKAGE_NAME || "Paket " + pkg.PACKAGEID }}</div>
                                      <div class="caption grey--text">{{ pkg.ARRANGEMENT_CODE || "-" }}</div>
                                    </td>
                                    <td>{{ formatDateOnly(pkg.ARRIVAL) }} – {{ formatDateOnly(pkg.DEPARTURE) }}</td>
                                    <td>{{ pkg.RESSORTGROUPID || "-" }}</td>
                                    <td>{{ formatDateOnly(pkg.PAIDDATE) }}</td>
                                    <td>State {{ pkg.PACKAGE_STATE }}</td>
                                  </tr>
                                  <tr v-if="!(lookup.data.packages || []).length">
                                    <td colspan="6" class="empty-compact">Keine Pakete verknüpft.</td>
                                  </tr>
                                </tbody>
                              </v-simple-table>
                            </div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" lg="5">
                          <v-card dark class="pa-4 rounded-xl table-card fill-height">
                            <div class="section-heading mb-3">
                              <div>
                                <div class="eyebrow">R_T_COURSE_CUSTOMERS</div>
                                <h4 class="subtitle-2 font-weight-bold white--text">Kursbelegungen</h4>
                              </div>
                              <v-chip x-small color="grey darken-3">{{ (lookup.data.courses || []).length }}</v-chip>
                            </div>
                            <div v-for="course in lookup.data.courses || []" :key="course.COURSE_CUSTOMERID" class="compact-record align-start">
                              <v-icon color="#7bb8ff" class="mr-3">mdi-account-school-outline</v-icon>
                              <div class="flex-grow-1 min-width-0">
                                <div class="font-weight-bold white--text">
                                  {{ course.COURSE_NAME || course.TEMPLATE_NAME || "Kurs " + course.COURSEID }}
                                </div>
                                <div class="caption grey--text">
                                  Belegung {{ course.COURSE_CUSTOMERID }} · Studio {{ course.RESSORTGROUPID || "-" }}
                                </div>
                                <div class="caption grey--text">Preis CHF {{ formatMoney(course.PRICE) }} · Paid state {{ course.PAID_STATE }}</div>
                              </div>
                            </div>
                            <div v-if="!(lookup.data.courses || []).length" class="empty-compact">Keine Kursbelegungen verknüpft.</div>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-tab-item>

                    <!-- 9. CRM and profile relations -->
                    <v-tab-item>
                      <div class="d-flex flex-column gap-4">
                        <v-row>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <div>
                                  <div class="eyebrow">Profile.Addresses</div>
                                  <h4 class="subtitle-2 font-weight-bold white--text">Weitere Adressen</h4>
                                </div>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.addresses || []).length }}</v-chip>
                              </div>
                              <div v-for="address in lookup.data.addresses || []" :key="address.CUSTOMERADDRESSID" class="compact-record align-start">
                                <v-icon color="#fbc02d" class="mr-3">mdi-map-marker-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">
                                    {{ address.ADDRESS_TYPE_DESCRIPTION || address.ADDRESS_TYPE || "Adresse" }}
                                    <span v-if="address.PRIMARY" class="caption yellow--text">· Primär</span>
                                  </div>
                                  <div class="caption grey--text">{{ address.ADDRESS1 }} {{ address.ADDRESS2 }}</div>
                                  <div class="caption grey--text">{{ address.ZIPCODE }} {{ address.CITY }}, {{ address.COUNTRY }}</div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.addresses || []).length" class="empty-compact">Keine zusätzlichen Adressen.</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <div>
                                  <div class="eyebrow">Profile.Phones / Kommunikation</div>
                                  <h4 class="subtitle-2 font-weight-bold white--text">Kommunikationswege</h4>
                                </div>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.communications || []).length }}</v-chip>
                              </div>
                              <div
                                v-for="communication in lookup.data.communications || []"
                                :key="communication.CUSTOMERCOMMUNICATIONID"
                                class="compact-record">
                                <v-icon color="#64d98b" class="mr-3">mdi-message-text-outline</v-icon>
                                <div class="flex-grow-1 min-width-0">
                                  <div class="font-weight-bold white--text text-truncate">{{ communication.VALUE || "-" }}</div>
                                  <div class="caption grey--text">
                                    {{
                                      communication.COMMUNICATION_TYPE_DESCRIPTION ||
                                      communication.COMMUNICATION_TYPE ||
                                      "Typ " + communication.COMMUNICATIONTYPEID
                                    }}<span v-if="communication.PRIMARY"> · Primär</span>
                                  </div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.communications || []).length" class="empty-compact">Keine weiteren Kommunikationswege.</div>
                            </v-card>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="eyebrow">Profile.SegmentCodes</div>
                              <h4 class="subtitle-2 font-weight-bold white--text mb-3">Segmente</h4>
                              <div class="d-flex flex-wrap gap-2">
                                <v-chip
                                  v-for="segment in lookup.data.segments || []"
                                  :key="segment.SEGMENTCODEID"
                                  small
                                  outlined
                                  color="#d7a8ff"
                                  label>
                                  <span class="font-weight-bold">{{ segment.SEGMENTCODENAME || segment.SEGMENTCODE }}</span>
                                  <span class="grey--text ml-1">· {{ segment.SEGMENTCODETYPENAME || "Segment" }}</span>
                                </v-chip>
                                <span v-if="!(lookup.data.segments || []).length" class="empty-compact">Keine Segmente zugeordnet.</span>
                              </div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="eyebrow">Profile.Origins</div>
                              <h4 class="subtitle-2 font-weight-bold white--text mb-3">Herkunft / Aufenthalte</h4>
                              <div v-for="origin in lookup.data.origins || []" :key="origin.CUSTOMERORIGINID" class="compact-record">
                                <v-icon color="#7bb8ff" class="mr-3">mdi-source-branch</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">{{ origin.ORIGINNAME || "Origin " + origin.ORIGINID }}</div>
                                  <div class="caption grey--text">{{ formatDateOnly(origin.ARRIVAL) }} – {{ formatDateOnly(origin.DEPARTURE) }}</div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.origins || []).length" class="empty-compact">Keine Herkunftsdaten verknüpft.</div>
                            </v-card>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <div>
                                  <div class="eyebrow">Profile.BankAccounts · maskiert</div>
                                  <h4 class="subtitle-2 font-weight-bold white--text">Bankverbindungen</h4>
                                </div>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.bankDetails || []).length }}</v-chip>
                              </div>
                              <div v-for="bank in lookup.data.bankDetails || []" :key="bank.BANKDETAILID" class="compact-record">
                                <v-icon color="#64d98b" class="mr-3">mdi-bank-outline</v-icon>
                                <div class="flex-grow-1 min-width-0">
                                  <div class="font-weight-bold white--text text-truncate">
                                    {{ bank.DESCRIPTION || bank.BANK || "Bankverbindung"
                                    }}<span v-if="bank.DEFAULT" class="caption yellow--text"> · Standard</span>
                                  </div>
                                  <div class="caption grey--text">
                                    IBAN •••• {{ bank.IBAN_LAST4 || "----" }} · {{ bank.OWNERNAME || "Inhaber unbekannt" }}
                                  </div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.bankDetails || []).length" class="empty-compact">
                                Keine Bankverbindungen. Vollständige Kontonummern werden hier nicht ausgegeben.
                              </div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" lg="6">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <div>
                                  <div class="eyebrow">Profile.Documents · maskiert</div>
                                  <h4 class="subtitle-2 font-weight-bold white--text">Dokumente</h4>
                                </div>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.documents || []).length }}</v-chip>
                              </div>
                              <div v-for="document in lookup.data.documents || []" :key="document.CUSTOMERDOCUMENTID" class="compact-record">
                                <v-icon color="#7bb8ff" class="mr-3">mdi-card-account-details-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">
                                    {{ document.DOCUMENT_TYPE_DESCRIPTION || document.DOCUMENT_TYPE || "Dokumenttyp " + document.DOCUMENTTYPEID }}
                                  </div>
                                  <div class="caption grey--text">
                                    Nummer •••• {{ document.DOCUMENT_NUMBER_LAST4 || "----" }} · gültig bis
                                    {{ formatDateOnly(document.EXPIRATIONDATE) }}
                                  </div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.documents || []).length" class="empty-compact">Keine Dokumentmetadaten.</div>
                            </v-card>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" xl="4">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Kommentare</h4>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.comments || []).length }}</v-chip>
                              </div>
                              <div
                                v-for="comment in (lookup.data.comments || []).slice(0, 20)"
                                :key="comment.CUSTOMER_COMMENTID"
                                class="compact-record align-start">
                                <v-icon color="#fbc02d" class="mr-3">mdi-note-text-outline</v-icon>
                                <div>
                                  <div class="caption yellow--text">{{ comment.COMMENT_TYPE || comment.COMMENT_CODE || "Notiz" }}</div>
                                  <div class="body-2 white--text">{{ comment.TEXT || "-" }}</div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.comments || []).length" class="empty-compact">Keine Kommentare.</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" md="6" xl="4">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Opportunities</h4>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.opportunities || []).length }}</v-chip>
                              </div>
                              <div
                                v-for="opportunity in (lookup.data.opportunities || []).slice(0, 20)"
                                :key="opportunity.OPPORTUNITYID"
                                class="compact-record align-start">
                                <v-icon color="#64d98b" class="mr-3">mdi-target-account</v-icon>
                                <div class="flex-grow-1">
                                  <div class="font-weight-bold white--text">
                                    {{ opportunity.DESCRIPTION || opportunity.OPPORTUNITY_TYPE || "Opportunity" }}
                                  </div>
                                  <div class="caption grey--text">
                                    {{ opportunity.OPPORTUNITY_STATE || "Offen" }} · {{ Math.round((opportunity.PROBABILITY || 0) * 100) }}% ·
                                    {{ formatDateOnly(opportunity.CREATIONDATE) }}
                                  </div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.opportunities || []).length" class="empty-compact">Keine Opportunities.</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" md="6" xl="4">
                            <v-card dark class="pa-4 rounded-xl table-card fill-height">
                              <div class="section-heading mb-3">
                                <h4 class="subtitle-2 font-weight-bold white--text">Korrespondenz</h4>
                                <v-chip x-small color="grey darken-3">{{ (lookup.data.correspondences || []).length }}</v-chip>
                              </div>
                              <div
                                v-for="item in (lookup.data.correspondences || []).slice(0, 20)"
                                :key="item.CORRESPONDENCEID"
                                class="compact-record align-start">
                                <v-icon color="#7bb8ff" class="mr-3">mdi-email-fast-outline</v-icon>
                                <div class="flex-grow-1 min-width-0">
                                  <div class="font-weight-bold white--text text-truncate">
                                    {{ item.SUMMARY || item.CORRESPONDENCE_TYPE || "Korrespondenz" }}
                                  </div>
                                  <div class="caption grey--text text-truncate">{{ formatDate(item.CREATIONDATE) }} · State {{ item.STATE }}</div>
                                </div>
                              </div>
                              <div v-if="!(lookup.data.correspondences || []).length" class="empty-compact">Keine Korrespondenz.</div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-tab-item>

                    <!-- 10. History / Yearly Mapped tables -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <h4 class="subtitle-2 font-weight-bold white--text mb-4">Historical & Yearly Tables</h4>

                        <v-expansion-panels accordion dark flat class="custom-expansion-panels">
                          <v-expansion-panel
                            v-for="hTable in lookup.data.historyData"
                            :key="hTable.table"
                            class="mb-3 rounded-lg border-panel"
                            style="background-color: rgba(255, 255, 255, 0.02)">
                            <v-expansion-panel-header class="py-3 px-4 font-weight-bold">
                              <div class="d-flex align-center justify-space-between w-100 pr-4">
                                <span class="yellow--text text--darken-1 text-subtitle-2 font-weight-black">{{ hTable.table }}</span>
                                <v-chip x-small color="grey darken-2" class="grey--text text--lighten-2 font-weight-bold">
                                  {{ hTable.rows.length }} rows found ({{ hTable.columnUsed }})
                                </v-chip>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="px-4 pb-4">
                              <div class="text-right mb-2">
                                <v-btn
                                  x-small
                                  color="#fbc02d"
                                  class="black--text font-weight-bold"
                                  @click="exportLookupTableToCSV(hTable.rows, hTable.table)">
                                  <v-icon left x-small>mdi-download</v-icon> Export CSV
                                </v-btn>
                              </div>
                              <div style="overflow-x: auto">
                                <v-simple-table dark class="detail-table">
                                  <thead>
                                    <tr>
                                      <th v-for="col in getHistoryTableHeaders(hTable.rows)" :key="col">{{ col }}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(row, idx) in hTable.rows.slice(0, 50)" :key="idx">
                                      <td
                                        v-for="col in getHistoryTableHeaders(hTable.rows)"
                                        :key="col"
                                        class="text-truncate"
                                        style="max-width: 150px">
                                        {{ formatValue(row[col]) }}
                                      </td>
                                    </tr>
                                    <tr v-if="hTable.rows.length > 50">
                                      <td :colspan="getHistoryTableHeaders(hTable.rows).length" class="text-center caption grey--text py-3">
                                        Showing top 50 rows. Click Export CSV to view all.
                                      </td>
                                    </tr>
                                  </tbody>
                                </v-simple-table>
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>

                        <div v-if="lookup.data.historyData.length === 0" class="text-center py-6 grey--text">
                          No matching records in historical or yearly database tables.
                        </div>
                      </v-card>
                    </v-tab-item>
                  </template>

                  <!-- LOCATION DETAILS RENDER -->
                  <template v-else-if="lookup.selectedEntity.type === 'location'">
                    <!-- 1. Resorts -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">G_M_RESSORTS (via M_A_LOCATION_RESSORTS)</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.resorts, 'location_resorts')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Resort ID</th>
                              <th>Name</th>
                              <th>Full Name</th>
                              <th>Resort Group ID</th>
                              <th>Bill Pretext</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="r in lookup.data.resorts" :key="r.RESSORTID">
                              <td class="font-weight-bold yellow--text text--lighten-2">{{ r.RESSORTID }}</td>
                              <td>{{ r.NAME }}</td>
                              <td>{{ r.FULL_NAME }}</td>
                              <td>{{ r.RESSORTGROUPID }}</td>
                              <td>{{ r.BILLNOPRETEXT }}</td>
                            </tr>
                            <tr v-if="lookup.data.resorts.length === 0">
                              <td colspan="5" class="text-center py-6 grey--text">No resorts linked.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 2. Configurations -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card mb-4">
                        <h4 class="subtitle-2 yellow--text text--darken-2 font-weight-bold text-uppercase mb-3">
                          M_A_ACCESSCONFIGURATION_SURCHARGELOCATIONS
                        </h4>
                        <v-simple-table dark class="detail-table mb-4">
                          <thead>
                            <tr>
                              <th>Access Config ID</th>
                              <th>Location ID</th>
                              <th>Surcharge Article ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, idx) in lookup.data.accessConfigSurcharges" :key="'sc-' + idx">
                              <td>{{ item.ACCESSCONFIGURATIONID }}</td>
                              <td>{{ item.LOCATIONID }}</td>
                              <td>{{ item.SURCHARGE_KASSA_ARTICLEID }}</td>
                            </tr>
                            <tr v-if="lookup.data.accessConfigSurcharges.length === 0">
                              <td colspan="3" class="text-center py-4 grey--text">No records.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>

                        <h4 class="subtitle-2 yellow--text text--darken-2 font-weight-bold text-uppercase mb-3">
                          M_A_ACCESSDEVICE_USAGE_ADDITIONAL_LOCATIONS
                        </h4>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Device ID</th>
                              <th>Additional Location ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, idx) in lookup.data.accessDeviceUsageAdditional" :key="'dev-' + idx">
                              <td>{{ item.ACCESSDEVICE_USAGEID }}</td>
                              <td>{{ item.ADDITIONAL_LOCATIONID }}</td>
                            </tr>
                            <tr v-if="lookup.data.accessDeviceUsageAdditional.length === 0">
                              <td colspan="2" class="text-center py-4 grey--text">No records.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 3. Checkins -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">CHECKINS (Combined M_A_CHECKINS_*, Top 200)</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.checkins, 'location_checkins')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Checkin ID</th>
                              <th>Customer ID</th>
                              <th>Checkin Date</th>
                              <th>Checkout Date</th>
                              <th>Key Code</th>
                              <th>Source Table</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="c in lookup.data.checkins" :key="c.CHECKINID + '-' + c._sourceTable">
                              <td>{{ c.CHECKINID }}</td>
                              <td class="font-weight-bold yellow--text text--lighten-2">{{ c.CUSTOMERID }}</td>
                              <td class="green--text text--lighten-2">{{ formatDate(c.CHECKINDATE) }}</td>
                              <td class="grey--text">{{ formatDate(c.CHECKOUTDATE) }}</td>
                              <td>{{ c.KEY || "-" }}</td>
                              <td>
                                <v-chip x-small color="grey darken-3" outlined class="grey--text text--lighten-1">
                                  {{ c._sourceTable }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.checkins.length === 0">
                              <td colspan="6" class="text-center py-6 grey--text">No checkins registered at this location.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>
                  </template>

                  <!-- RESORT GROUP DETAILS RENDER -->
                  <template v-else-if="lookup.selectedEntity.type === 'ressortgroup'">
                    <!-- 1. Abo-Strukturen -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">M_M_CATEGORIES & W_M_CATEGORIES</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(displayedContractCategories, 'group_abo_structures')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <!-- Switch to show all categories or only filtered ones -->
                        <div class="d-flex align-center mb-4">
                          <v-switch
                            v-model="lookup.showAllCategories"
                            label="Alle Vertragsmodelle der Marke anzeigen (anstatt nur diesem Studio zugeordnete)"
                            dense
                            dark
                            hide-details
                            class="mt-0 pt-0"
                            color="yellow darken-2"></v-switch>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Name (Webshop Name)</th>
                              <th>Typ (Haupt / Zusatz)</th>
                              <th>Laufzeit</th>
                              <th>Beitrag / Gebühr</th>
                              <th>Mindestlaufzeit</th>
                              <th>Kündigungsfrist</th>
                              <th>Voraussetzungen (Zusatzpaket-Bedingungen)</th>
                              <th>Zuordnung (Kanal)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(groupCats, groupName) in groupedContractCategories">
                              <!-- Group Header Row -->
                              <tr :key="'group-header-' + groupName" style="background-color: rgba(251, 192, 45, 0.08)">
                                <td colspan="9" class="font-weight-bold py-2 yellow--text text--accent-2 text-subtitle-2">
                                  <v-icon small color="#fbc02d" class="mr-2">mdi-folder-open</v-icon>
                                  {{ groupName }} ({{ groupCats.length }})
                                </td>
                              </tr>

                              <!-- Group Rows -->
                              <tr v-for="cat in groupCats" :key="cat.CATEGORYID">
                                <td class="font-weight-bold yellow--text text--lighten-2">{{ cat.CATEGORYID }}</td>
                                <td>
                                  <div class="font-weight-bold white--text">{{ cat.CATEGORY_NAME || "-" }}</div>
                                  <div class="caption grey--text" v-if="cat.WEBSHOP_NAME && cat.WEBSHOP_NAME !== cat.CATEGORY_NAME">
                                    Webshop: {{ cat.WEBSHOP_NAME }}
                                  </div>
                                  <div class="caption grey--text italic" v-if="cat.CATEGORY_DESC" style="max-width: 250px; white-space: normal">
                                    {{ cat.CATEGORY_DESC }}
                                  </div>
                                </td>
                                <td>
                                  <v-chip
                                    x-small
                                    :color="cat.usage_sub ? 'purple accent-2' : 'teal accent-3'"
                                    class="black--text font-weight-bold"
                                    label>
                                    {{ cat.usage_sub ? "Zusatzpaket" : "Hauptvertrag" }}
                                  </v-chip>
                                </td>
                                <td>{{ cat.PERIOD }} {{ formatUnit(cat.UNIT) }}</td>
                                <td>
                                  <div v-for="(price, idx) in getCategoryPrices(cat.CATEGORYID)" :key="idx" class="caption white--text my-1">
                                    <span class="font-weight-bold yellow--text text--lighten-2">{{ price.val }} CHF</span>
                                    <span class="grey--text"> ({{ price.interval }})</span>
                                    <div
                                      class="caption grey--text x-small"
                                      style="line-height: 1.1"
                                      v-if="price.desc && price.desc !== cat.CATEGORY_NAME && price.desc !== cat.GROUP_NAME">
                                      {{ price.desc }}
                                    </div>
                                  </div>
                                  <span class="caption grey--text" v-if="getCategoryPrices(cat.CATEGORYID).length === 0">-</span>
                                </td>
                                <td>{{ cat.COMMITMENTPERIOD }} {{ formatUnit(cat.UNIT) }}</td>
                                <td>{{ cat.CANCELLATIONLIMIT }} Tage</td>
                                <td>
                                  <!-- Type-dependent requirements / possibilities -->
                                  <div v-if="cat.usage_sub || cat.checkmaincontract">
                                    <!-- Sub contract / Zusatzpaket: Show required main contracts -->
                                    <div
                                      class="caption red--text text--lighten-2 font-weight-bold"
                                      v-if="getCategoryDependencies(cat.CATEGORYID).length === 0">
                                      <v-icon x-small color="red lighten-2" class="mr-1">mdi-alert-circle-outline</v-icon>
                                      Erfordert Hauptvertrag (generisch)
                                    </div>
                                    <div v-else>
                                      <div class="caption grey--text font-weight-bold">Benötigt Hauptvertrag:</div>
                                      <div
                                        v-for="dep in getCategoryDependencies(cat.CATEGORYID)"
                                        :key="dep.Required_CategoryID"
                                        class="caption yellow--text text--darken-3 font-weight-bold">
                                        • {{ dep.RequiredCategoryName }} (ID: {{ dep.Required_CategoryID }})
                                      </div>
                                    </div>
                                  </div>
                                  <div v-else>
                                    <!-- Main contract / Hauptvertrag: Show possible Zusatzpakete that can be added -->
                                    <div v-if="getAvailableZusatzForMain(cat.CATEGORYID).length > 0">
                                      <div class="caption grey--text font-weight-bold">Mögliche Zusatzpakete:</div>
                                      <div
                                        v-for="sub in getAvailableZusatzForMain(cat.CATEGORYID)"
                                        :key="sub.CategoryID"
                                        class="caption purple--text text--lighten-3 font-weight-bold">
                                        • {{ getCategoryNameById(sub.CategoryID) }} (ID: {{ sub.CategoryID }})
                                      </div>
                                    </div>
                                    <span class="caption grey--text" v-else>Keine Zusatzpakete verknüpft</span>
                                  </div>
                                </td>
                                <td>
                                  <v-chip x-small :color="getCategoryWebshopStatus(cat.CATEGORYID).color" class="black--text font-weight-bold" label>
                                    {{ getCategoryWebshopStatus(cat.CATEGORYID).text }}
                                  </v-chip>
                                </td>
                              </tr>
                            </template>
                            <tr v-if="displayedContractCategories.length === 0">
                              <td colspan="9" class="text-center py-6 grey--text">Keine Abonnements / Vertragsstrukturen aufgeschaltet.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 2. Resorts -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">G_M_RESSORTS</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.resorts, 'group_resorts')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Resort ID</th>
                              <th>Name</th>
                              <th>Full Name</th>
                              <th>Bill Pretext</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="r in lookup.data.resorts" :key="r.RESSORTID">
                              <td class="font-weight-bold yellow--text text--lighten-2">{{ r.RESSORTID }}</td>
                              <td>{{ r.NAME }}</td>
                              <td>{{ r.FULL_NAME }}</td>
                              <td>{{ r.BILLNOPRETEXT }}</td>
                            </tr>
                            <tr v-if="lookup.data.resorts.length === 0">
                              <td colspan="4" class="text-center py-6 grey--text">No resorts in this group.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 2. Locations -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">M_M_LOCATIONS</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.locations, 'group_locations')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Location ID</th>
                              <th>Short Name</th>
                              <th>Name</th>
                              <th>Description</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="l in lookup.data.locations" :key="l.LOCATIONID">
                              <td class="font-weight-bold yellow--text text--lighten-2">{{ l.LOCATIONID }}</td>
                              <td>{{ l.SHORTNAME }}</td>
                              <td>{{ l.NAME }}</td>
                              <td class="text-truncate" style="max-width: 200px">{{ l.DESCRIPTION || "-" }}</td>
                              <td>
                                <v-chip x-small :color="l.INACTIVE ? 'red accent-2' : 'green accent-3'" class="black--text font-weight-bold">
                                  {{ l.INACTIVE ? "Inactive" : "Active" }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.locations.length === 0">
                              <td colspan="5" class="text-center py-6 grey--text">No locations in this group.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 3. Contracts -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">M_T_CONTRACTS (Top 100)</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.contracts, 'group_contracts')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Contract ID</th>
                              <th>Customer ID</th>
                              <th>State</th>
                              <th>Start Date</th>
                              <th>End Date</th>
                              <th>Termination Date</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="c in lookup.data.contracts" :key="c.CONTRACTID">
                              <td class="font-weight-bold yellow--text text--lighten-2">{{ c.CONTRACTID }}</td>
                              <td class="font-weight-bold">{{ c.CUSTOMERID }}</td>
                              <td>
                                <v-chip x-small :color="getContractStateColor(c.CONTRACTSTATE)" class="black--text font-weight-bold" label>
                                  {{ getContractStateText(c.CONTRACTSTATE) }}
                                </v-chip>
                              </td>
                              <td>{{ formatDateOnly(c.STARTDATE) }}</td>
                              <td>{{ formatDateOnly(c.ENDDATE) }}</td>
                              <td>{{ formatDateOnly(c.TERMINATE_DATE) }}</td>
                              <td class="text-truncate" style="max-width: 150px">{{ c.TERMINATE_REASON || "-" }}</td>
                            </tr>
                            <tr v-if="lookup.data.contracts.length === 0">
                              <td colspan="7" class="text-center py-6 grey--text">No contracts registered.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 4. Bills -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">B_T_BILLS (Top 100)</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.bills, 'group_bills')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Bill ID</th>
                              <th>Customer ID</th>
                              <th>Bill No</th>
                              <th>Bill Date</th>
                              <th>Total Amount</th>
                              <th>Storno</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="b in lookup.data.bills" :key="b.BILLID">
                              <td class="font-weight-bold">{{ b.BILLID }}</td>
                              <td>{{ b.CUSTOMERID }}</td>
                              <td>{{ b.BILLNO || "-" }}</td>
                              <td>{{ formatDate(b.BILLDATE) }}</td>
                              <td class="font-weight-bold white--text">CHF {{ parseFloat(b.TOTALAMOUNT || 0).toFixed(2) }}</td>
                              <td>
                                <v-chip x-small :color="b.STORNO ? 'red accent-2' : 'grey darken-3'" class="black--text font-weight-bold">
                                  {{ b.STORNO ? "Storno" : "No" }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.bills.length === 0">
                              <td colspan="6" class="text-center py-6 grey--text">No bills recorded.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>

                    <!-- 5. Checkins -->
                    <v-tab-item>
                      <v-card dark class="pa-4 rounded-xl table-card">
                        <div class="d-flex justify-space-between align-center mb-3">
                          <h4 class="subtitle-2 font-weight-bold white--text mb-0">CHECKINS (Combined M_A_CHECKINS_*, Top 200)</h4>
                          <v-btn
                            x-small
                            color="#fbc02d"
                            class="black--text font-weight-bold"
                            @click="exportLookupTableToCSV(lookup.data.checkins, 'group_checkins')">
                            <v-icon left x-small>mdi-download</v-icon> Export CSV
                          </v-btn>
                        </div>
                        <v-simple-table dark class="detail-table">
                          <thead>
                            <tr>
                              <th>Checkin ID</th>
                              <th>Customer ID</th>
                              <th>Location ID</th>
                              <th>Checkin Date</th>
                              <th>Checkout Date</th>
                              <th>Source Table</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="c in lookup.data.checkins" :key="c.CHECKINID + '-' + c._sourceTable">
                              <td>{{ c.CHECKINID }}</td>
                              <td class="font-weight-bold yellow--text text--lighten-2">{{ c.CUSTOMERID }}</td>
                              <td>{{ c.LOCATIONID }}</td>
                              <td class="green--text text--lighten-2">{{ formatDate(c.CHECKINDATE) }}</td>
                              <td class="grey--text">{{ formatDate(c.CHECKOUTDATE) }}</td>
                              <td>
                                <v-chip x-small color="grey darken-3" outlined class="grey--text text--lighten-1">
                                  {{ c._sourceTable }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr v-if="lookup.data.checkins.length === 0">
                              <td colspan="6" class="text-center py-6 grey--text">No checkins registered in this ressort group.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card>
                    </v-tab-item>
                  </template>
                </v-tabs-items>
              </div>

              <!-- Welcome empty state -->
              <v-card
                v-else
                dark
                class="control-card pa-12 rounded-xl text-center fill-height d-flex flex-column align-center justify-center"
                style="min-height: 400px">
                <v-icon size="64" color="grey darken-3" class="mb-3">mdi-vector-link</v-icon>
                <h3 class="text-subtitle-1 grey--text text--lighten-1 font-weight-bold">No Entity Selected</h3>
                <p class="grey--text text-caption mt-2" style="max-width: 320px">
                  Use the left search panel to look up a Member, Location, or Studio, and build a complete raw table relationship map.
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <!-- Customer Profile & History Dialog -->
    <v-dialog v-model="detailDialog.show" max-width="1200px" scrollable overlay-color="#000" overlay-opacity="0.8">
      <v-card dark class="pa-4 rounded-xl border-highlight" style="background-color: #1a1a1c">
        <!-- Dialog Header -->
        <v-card-title class="d-flex align-center justify-space-between pb-4 border-bottom">
          <div class="d-flex align-center">
            <v-avatar color="#fbc02d" size="48" class="mr-3 black--text font-weight-black">
              {{ getInitials() }}
            </v-avatar>
            <div>
              <h3 class="text-h5 font-weight-bold white--text">
                {{ getCustomerFullName() }}
              </h3>
              <span class="caption grey--text text-uppercase font-weight-bold"> Customer Profile • ID: {{ detailDialog.customerId }} </span>
            </div>
          </div>
          <v-btn icon color="grey lighten-1" @click="detailDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Dialog Content -->
        <v-card-text class="py-4 px-1" style="height: 700px">
          <!-- Detail Loading Spinner -->
          <div v-if="detailDialog.loading" class="d-flex flex-column align-center justify-center my-12 py-12">
            <v-progress-circular indeterminate color="#fbc02d" size="64" width="6" class="mb-4"></v-progress-circular>
            <div class="subtitle-1 grey--text">Fetching linked database records...</div>
          </div>

          <!-- Detail Profile Panel -->
          <div v-else-if="detailDialog.data">
            <!-- Profile Info Row -->
            <v-row class="mb-6">
              <!-- Contact Summary -->
              <v-col cols="12" md="8">
                <v-card class="stat-card pa-4 rounded-xl fill-height">
                  <h4 class="subtitle-2 yellow--text text--darken-2 font-weight-bold text-uppercase mb-3">
                    <v-icon small color="#fbc02d" class="mr-1">mdi-card-account-details-outline</v-icon> Contact & Base Metadata
                  </h4>
                  <v-row dense>
                    <v-col cols="12" sm="6" v-for="info in profileFields" :key="info.label">
                      <div class="d-flex align-center py-1">
                        <v-icon small class="grey--text mr-2" style="width: 20px">{{ info.icon }}</v-icon>
                        <div>
                          <div class="caption grey--text text-uppercase">{{ info.label }}</div>
                          <div class="body-2 white--text font-weight-bold">{{ info.value || "-" }}</div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>

              <!-- Accounts Summary Widget -->
              <v-col cols="12" md="4">
                <v-card class="stat-card pa-4 rounded-xl fill-height d-flex flex-column justify-space-between">
                  <div>
                    <h4 class="subtitle-2 yellow--text text--darken-2 font-weight-bold text-uppercase mb-3">
                      <v-icon small color="#fbc02d" class="mr-1">mdi-wallet-outline</v-icon> Financial Summary
                    </h4>
                    <div class="caption grey--text text-uppercase">Total Accounts Balance</div>
                    <div
                      class="text-h3 font-weight-black my-2"
                      :class="totalBalance >= 0 ? 'green--text text--accent-3' : 'red--text text--accent-2'">
                      CHF {{ totalBalance.toFixed(2) }}
                    </div>
                  </div>
                  <div class="caption grey--text">Summed from {{ detailDialog.data.accounts.length }} active database accounts</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Interactive Sub-Tabs -->
            <v-tabs v-model="detailDialog.activeTab" background-color="transparent" color="#fbc02d" grow class="custom-tabs rounded-lg mb-4">
              <v-tab class="font-weight-bold">
                <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                Contracts ({{ detailDialog.data.contracts.length }})
              </v-tab>
              <v-tab class="font-weight-bold">
                <v-icon small class="mr-1">mdi-bank-transfer</v-icon>
                Transactions ({{ detailDialog.data.transactions.length }})
              </v-tab>
              <v-tab class="font-weight-bold">
                <v-icon small class="mr-1">mdi-card-bulleted-outline</v-icon>
                Accounts ({{ detailDialog.data.accounts.length }})
              </v-tab>
              <v-tab class="font-weight-bold">
                <v-icon small class="mr-1">mdi-receipt-text-outline</v-icon>
                Bills ({{ detailDialog.data.bills.length }})
              </v-tab>
              <v-tab class="font-weight-bold">
                <v-icon small class="mr-1">mdi-map-marker-radius-outline</v-icon>
                Checkins ({{ detailDialog.data.checkins.length }})
              </v-tab>
            </v-tabs>

            <!-- Tab Items Content -->
            <v-tabs-items v-model="detailDialog.activeTab" style="background-color: transparent">
              <!-- 1. Contracts Tab -->
              <v-tab-item>
                <v-card class="pa-4 rounded-xl stat-card">
                  <v-simple-table dark class="detail-table">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold text-uppercase">Contract ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Container ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Contract Type</th>
                        <th class="text-left font-weight-bold text-uppercase">State</th>
                        <th class="text-left font-weight-bold text-uppercase">Use Start</th>
                        <th class="text-left font-weight-bold text-uppercase">Use End</th>
                        <th class="text-left font-weight-bold text-uppercase">Terminated Date</th>
                        <th class="text-left font-weight-bold text-uppercase">Termination Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in detailDialog.data.contracts" :key="c.CONTRACTID">
                        <td class="font-weight-bold yellow--text text--lighten-2">{{ c.CONTRACTID }}</td>
                        <td>{{ c.CONTRACTCONTAINERID || "-" }}</td>
                        <td>{{ c.CONTRACTTYP || "Standard" }}</td>
                        <td>
                          <v-chip x-small :color="getContractStateColor(c.CONTRACTSTATE)" class="black--text font-weight-bold" label>
                            {{ getContractStateText(c.CONTRACTSTATE) }}
                          </v-chip>
                        </td>
                        <td>{{ formatDate(c.USE_STARTDATE) }}</td>
                        <td>{{ formatDate(c.USE_ENDDATE) }}</td>
                        <td>{{ formatDate(c.TERMINATEDATE) }}</td>
                        <td>{{ c.TERMINATE_REASON || "-" }}</td>
                      </tr>
                      <tr v-if="detailDialog.data.contracts.length === 0">
                        <td colspan="8" class="text-center py-6 grey--text">No contracts registered for this customer.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-tab-item>

              <!-- 2. Transactions Tab -->
              <v-tab-item>
                <v-card class="pa-4 rounded-xl stat-card">
                  <v-simple-table dark class="detail-table">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold text-uppercase">Transaction ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Date</th>
                        <th class="text-left font-weight-bold text-uppercase">Type</th>
                        <th class="text-left font-weight-bold text-uppercase">Description</th>
                        <th class="text-left font-weight-bold text-uppercase">Value</th>
                        <th class="text-left font-weight-bold text-uppercase">Account Name</th>
                        <th class="text-left font-weight-bold text-uppercase">Source Resort</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="t in detailDialog.data.transactions" :key="t.ACCOUNT_TRANSACTIONID">
                        <td>{{ t.ACCOUNT_TRANSACTIONID }}</td>
                        <td>{{ formatDate(t.BILLDATE) }}</td>
                        <td>
                          <v-chip x-small outlined color="grey lighten-1">
                            {{ t.AccountTransactionType || "Payment" }}
                          </v-chip>
                        </td>
                        <td class="text-truncate" style="max-width: 250px">{{ t.DESCRIPTION }}</td>
                        <td class="font-weight-black" :class="t.VALUE >= 0 ? 'green--text text--accent-3' : 'red--text text--accent-2'">
                          CHF {{ parseFloat(t.VALUE || 0).toFixed(2) }}
                        </td>
                        <td>{{ t.AccountName || "-" }}</td>
                        <td>{{ t.SourceRessortgroupName || "-" }}</td>
                      </tr>
                      <tr v-if="detailDialog.data.transactions.length === 0">
                        <td colspan="7" class="text-center py-6 grey--text">No transactions logged.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-tab-item>

              <!-- 3. Accounts Tab -->
              <v-tab-item>
                <v-card class="pa-4 rounded-xl stat-card">
                  <v-simple-table dark class="detail-table">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold text-uppercase">Account ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Account Type ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Resort Group ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Balance</th>
                        <th class="text-left font-weight-bold text-uppercase">First Unpaid Booking</th>
                        <th class="text-left font-weight-bold text-uppercase">Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="a in detailDialog.data.accounts" :key="a.ACCOUNTID">
                        <td class="font-weight-bold">{{ a.ACCOUNTID }}</td>
                        <td>{{ a.ACCOUNTTYPEID }}</td>
                        <td>{{ a.RESSORTGROUPID }}</td>
                        <td class="font-weight-bold" :class="a.BALANCE >= 0 ? 'green--text text--accent-3' : 'red--text text--accent-2'">
                          CHF {{ parseFloat(a.BALANCE || 0).toFixed(2) }}
                        </td>
                        <td>{{ formatDate(a.FIRSTUNPAIDTRANSACTION) }}</td>
                        <td>{{ formatDate(a.DUEDATE) }}</td>
                      </tr>
                      <tr v-if="detailDialog.data.accounts.length === 0">
                        <td colspan="6" class="text-center py-6 grey--text">No account profiles linked.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-tab-item>

              <!-- 4. Bills Tab -->
              <v-tab-item>
                <v-card class="pa-4 rounded-xl stat-card">
                  <v-simple-table dark class="detail-table">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold text-uppercase">Bill ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Bill Date</th>
                        <th class="text-left font-weight-bold text-uppercase">Business Date</th>
                        <th class="text-left font-weight-bold text-uppercase">Total Amount</th>
                        <th class="text-left font-weight-bold text-uppercase">Kassa ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Resort Group ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="b in detailDialog.data.bills" :key="b.billid">
                        <td class="font-weight-bold">{{ b.billid }}</td>
                        <td>{{ formatDate(b.billdate) }}</td>
                        <td>{{ formatDate(b.businessdate) }}</td>
                        <td class="font-weight-bold white--text">CHF {{ parseFloat(b.totalamount || 0).toFixed(2) }}</td>
                        <td>{{ b.kassaid }}</td>
                        <td>{{ b.ressortgroupid }}</td>
                      </tr>
                      <tr v-if="detailDialog.data.bills.length === 0">
                        <td colspan="6" class="text-center py-6 grey--text">No bills recorded.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-tab-item>

              <!-- 5. Checkins Tab -->
              <v-tab-item>
                <v-card class="pa-4 rounded-xl stat-card">
                  <v-simple-table dark class="detail-table">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold text-uppercase">Checkin ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Checkin Time</th>
                        <th class="text-left font-weight-bold text-uppercase">Checkout Time</th>
                        <th class="text-left font-weight-bold text-uppercase">Location ID</th>
                        <th class="text-left font-weight-bold text-uppercase">Key Label</th>
                        <th class="text-left font-weight-bold text-uppercase">Access Device ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="chk in detailDialog.data.checkins" :key="chk.CHECKINID">
                        <td>{{ chk.CHECKINID }}</td>
                        <td class="green--text text--lighten-2">{{ formatDate(chk.CHECKINDATE) }}</td>
                        <td class="grey--text">{{ formatDate(chk.CHECKOUTDATE) }}</td>
                        <td>{{ chk.LOCATIONID }}</td>
                        <td>{{ chk.KEY || "-" }}</td>
                        <td>{{ chk.CHECKIN_ACCESSDEVICEID || "-" }}</td>
                      </tr>
                      <tr v-if="detailDialog.data.checkins.length === 0">
                        <td colspan="6" class="text-center py-6 grey--text">No checkins registered.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Database Schema Reference Guide Dialog -->
    <v-dialog v-model="schemaDialog.show" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card dark style="background-color: #0f0f0f">
        <!-- Header bar -->
        <v-toolbar dark color="#1c1c1e" flat class="border-bottom">
          <v-btn icon dark @click="schemaDialog.show = false">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-black italic-title yellow--text"> DATABASE SCHEMA REFERENCE </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-switch
            v-model="schemaDialog.hideReferences"
            label="Nur Haupttabellen"
            color="#fbc02d"
            hide-details
            dense
            class="mt-0 pt-0 mr-6"></v-switch>
          <v-text-field
            v-model="schemaDialog.search"
            append-icon="mdi-magnify"
            label="Search tables or descriptions..."
            single-line
            hide-details
            outlined
            dense
            dark
            color="#fbc02d"
            class="max-width-search rounded-lg mr-4"
            clearable
            style="max-width: 350px"></v-text-field>
          <v-btn icon @click="schemaDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Main content area -->
        <v-container fluid class="pa-6">
          <v-row>
            <!-- Left panel: Functional areas list -->
            <v-col cols="12" md="4" lg="3">
              <v-card class="control-card pa-4 rounded-xl fill-height">
                <h3 class="subtitle-1 font-weight-bold white--text mb-4 d-flex align-center">
                  <v-icon color="#fbc02d" class="mr-2">mdi-format-list-bulleted</v-icon>
                  Functional Modules
                </h3>
                <v-list dense bg-color="transparent" class="pa-0">
                  <v-list-item-group v-model="schemaDialog.activeCategoryIndex" mandatory color="#fbc02d">
                    <v-list-item v-for="cat in filteredSchemaCategories" :key="cat.id" class="rounded-lg mb-2" active-class="active-list-item">
                      <v-list-item-icon class="mr-3">
                        <v-icon>{{ cat.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold text-body-2">
                          {{ cat.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="grey--text caption"> {{ cat.tables.length }} tables/views </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card>
            </v-col>

            <!-- Right panel: Selected category's tables -->
            <v-col cols="12" md="8" lg="9">
              <v-card class="control-card pa-5 rounded-xl fill-height" style="min-height: 75vh">
                <div v-if="selectedSchemaCategory">
                  <div class="mb-5">
                    <div class="d-flex align-center mb-1">
                      <v-icon color="#fbc02d" class="mr-2 text-h5">{{ selectedSchemaCategory.icon }}</v-icon>
                      <h2 class="text-h5 font-weight-bold white--text">{{ selectedSchemaCategory.name }}</h2>
                    </div>
                    <p class="grey--text text-body-2 mb-0">{{ selectedSchemaCategory.description }}</p>
                  </div>

                  <v-divider class="grey darken-3 mb-5"></v-divider>

                  <v-expansion-panels accordion dark flat class="custom-expansion-panels">
                    <v-expansion-panel
                      v-for="table in selectedSchemaCategory.filteredTables"
                      :key="table"
                      class="mb-3 rounded-lg border-panel"
                      style="background-color: rgba(255, 255, 255, 0.02)"
                      @change="handleTableExpand(table)">
                      <v-expansion-panel-header class="py-3 px-4 font-weight-bold">
                        <template v-slot:default="{ open }">
                          <v-row no-gutters class="align-center">
                            <v-col cols="12" sm="8" class="d-flex align-center">
                              <span class="yellow--text text--darken-1 text-subtitle-1 font-weight-black mr-2">{{ table }}</span>
                              <v-chip v-if="table.startsWith('V_')" x-small color="blue" class="white--text font-weight-bold">VIEW</v-chip>
                              <v-chip v-else x-small color="grey darken-2" class="grey--text text--lighten-2 font-weight-bold">TABLE</v-chip>
                            </v-col>
                            <v-col cols="12" sm="4" class="text-right pr-4 mt-2 mt-sm-0" v-if="!open">
                              <v-btn x-small color="#fbc02d" class="black--text font-weight-bold rounded" @click.stop="loadTableIntoExplorer(table)">
                                Load in Explorer
                              </v-btn>
                            </v-col>
                          </v-row>
                        </template>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content class="px-4 pb-4">
                        <div class="body-2 grey--text text--lighten-2 mb-4">
                          {{ getTableDescription(table) }}
                        </div>

                        <!-- Column Metadata section -->
                        <div>
                          <div class="caption font-weight-bold text-uppercase grey--text mb-2 d-flex align-center justify-space-between">
                            <span>Columns & Schema Metadata</span>
                            <v-btn x-small color="#fbc02d" class="black--text font-weight-bold rounded" @click="loadTableIntoExplorer(table)">
                              Load in Explorer
                            </v-btn>
                          </div>

                          <div v-if="tableColumnsLoading[table]" class="d-flex align-center py-4">
                            <v-progress-circular indeterminate color="#fbc02d" size="20" width="2" class="mr-2"></v-progress-circular>
                            <span class="caption grey--text">Loading columns metadata...</span>
                          </div>

                          <v-chip-group column v-else-if="tableColumnsCache[table] && tableColumnsCache[table].length > 0">
                            <v-chip
                              v-for="col in tableColumnsCache[table]"
                              :key="col.name"
                              small
                              outlined
                              color="grey lighten-1"
                              class="ma-1 px-3 py-1 font-weight-bold rounded-lg">
                              <span class="white--text mr-1">{{ col.name }}</span>
                              <span class="yellow--text text--darken-3 caption">({{ col.type }})</span>
                            </v-chip>
                          </v-chip-group>

                          <div v-else class="caption grey--text italic">No column definitions found. Try loading the table.</div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <div v-if="selectedSchemaCategory.filteredTables.length === 0" class="text-center py-12 grey--text">
                    No tables/views matched your search query inside this module.
                  </div>
                </div>
                <div v-else class="d-flex align-center justify-center fill-height py-12">
                  <div class="text-center">
                    <v-icon size="64" color="grey darken-3">mdi-database-search</v-icon>
                    <div class="subtitle-1 grey--text mt-2">Select a functional module on the left to explore schemas.</div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { saveAs } from "file-saver"

export default {
  name: "TacLookupDashboard",
  data() {
    return {
      // Configuration & Control Panel
      tables: [],
      selectedTable: "V_CUSTOMER",
      searchQuery: "",
      errorAlert: null,

      // Loaders
      tablesLoading: false,
      loading: false,
      posSchemaDownloading: false,
      posMasterDataDownloading: false,
      posBookingExamplesDownloading: false,

      // Core explored data
      rows: [],
      columns: [],
      totalRows: 0,

      // Pagination & sorting configuration
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      searchTimeout: null,

      // Detail Profile Dialog State
      detailDialog: {
        show: false,
        customerId: null,
        loading: false,
        activeTab: 0,
        data: null,
      },

      // Schema Reference Guide State
      schemaDialog: {
        show: false,
        search: "",
        activeCategoryIndex: 0,
        hideReferences: true,
      },
      tableColumnsCache: {},
      tableColumnsLoading: {},

      // Tab management
      activeDashboardTab: 0,

      // Deep Relationship Lookup State
      lookup: {
        entityType: "customer",
        searchQuery: "",
        searchResults: [],
        searchLoading: false,
        selectedEntity: null,
        loading: false,
        activeTab: 0,
        data: null,
        expandedContracts: {},
        allBookingsDownloading: false,
        showAllCategories: false,
      },
      lookupSearchTimeout: null,
    }
  },
  computed: {
    // Dynamically builds v-data-table headers based on backend columns list
    headers() {
      return this.columns.map((c) => ({
        text: c.name,
        value: c.name,
        sortable: true,
      }))
    },
    // Filtered contract categories including required parents recursively
    displayedContractCategories() {
      if (!this.lookup.data || !this.lookup.data.contractCategories) return []
      const cats = this.lookup.data.contractCategories
      const assigns = this.lookup.data.contractAssignments || []
      const deps = this.lookup.data.contractDependencies || []
      const selectedStudioId = this.lookup.selectedEntity ? parseInt(this.lookup.selectedEntity.id, 10) : null

      if (!selectedStudioId) return cats

      // Get category IDs that are assigned to this studio
      const assignedIds = new Set(assigns.filter((a) => parseInt(a.RESSORTGROUPID, 10) === selectedStudioId).map((a) => a.CATEGORYID))

      if (this.lookup.showAllCategories) {
        return cats
      }

      // If filtering, we want:
      // 1. All assigned categories.
      // 2. Plus any categories that are required by these assigned categories (prerequisites).
      const finalIds = new Set(assignedIds)

      // Look up required parent categories for any category currently in finalIds
      let added = true
      let iterations = 0
      while (added && iterations < 10) {
        added = false
        iterations++
        deps.forEach((d) => {
          if (finalIds.has(d.CategoryID) && !finalIds.has(d.Required_CategoryID)) {
            finalIds.add(d.Required_CategoryID)
            added = true
          }
        })
      }

      return cats.filter((c) => finalIds.has(c.CATEGORYID))
    },
    // Groups contract categories by group name
    groupedContractCategories() {
      const list = this.displayedContractCategories
      const groups = {}
      list.forEach((c) => {
        const gName = c.GROUP_NAME || "Andere Verträge"
        if (!groups[gName]) {
          groups[gName] = []
        }
        groups[gName].push(c)
      })
      return groups
    },
    // Sums all bank account balances for the current profile
    totalBalance() {
      if (!this.detailDialog.data || !this.detailDialog.data.accounts) return 0
      return this.detailDialog.data.accounts.reduce((sum, acc) => sum + parseFloat(acc.BALANCE || 0), 0)
    },
    // Builds formatted fields array for easy display in details panel
    profileFields() {
      const p = this.detailDialog.data?.profile
      if (!p) return []

      // Determine casing for fields depending on backend responses (handles lowercase/uppercase queries)
      const getVal = (field) => {
        const key = Object.keys(p).find((k) => k.toUpperCase() === field.toUpperCase())
        return key ? p[key] : null
      }

      return [
        { label: "E-Mail Address", value: getVal("email") || getVal("emailaddress"), icon: "mdi-email" },
        { label: "Phone", value: getVal("phone") || getVal("phone2") || getVal("telefon"), icon: "mdi-phone" },
        { label: "Mobile Phone", value: getVal("mobilphone") || getVal("mobile") || getVal("handy"), icon: "mdi-cellphone" },
        { label: "Gender", value: this.formatGender(getVal("gender") || getVal("gender_pref")), icon: "mdi-gender-transgender" },
        { label: "Address", value: `${getVal("street") || ""} ${getVal("street2") || ""}`.trim(), icon: "mdi-map-marker" },
        { label: "City & Zip", value: `${getVal("zip_code") || ""} ${getVal("city") || ""}`.trim(), icon: "mdi-city" },
        { label: "Date of Birth", value: this.formatDateOnly(getVal("birthday") || getVal("birthdate") || getVal("geburtsdatum")), icon: "mdi-cake" },
        { label: "Creation Date", value: this.formatDate(getVal("creation_date") || getVal("temporarycreationdate")), icon: "mdi-calendar-plus" },
      ]
    },

    // Computed table categorization map
    schemaCategories() {
      const categories = [
        {
          id: "guest-attr",
          name: "Gäste-Metadaten (Guest Attributes - G_A_)",
          icon: "mdi-card-text-outline",
          description: "Adressen, Kommunikationswege, Bankverbindungen und Attribute für Gäste.",
          pattern: /^G_A_/i,
          tables: [],
        },
        {
          id: "guest-master",
          name: "Gäste-Stammdaten (Guest Master - G_M_)",
          icon: "mdi-account-box-multiple-outline",
          description: "Grundlegende Personenprofile, Haupt-Kundendatenbank und Suchindizes.",
          pattern: /^G_M_/i,
          tables: [],
        },
        {
          id: "guest-trans",
          name: "Gäste-Historie (Guest Transactions - G_T_)",
          icon: "mdi-history",
          description: "Verlaufshistorie der Gäste, Korrespondenzen, Logins und Notizen.",
          pattern: /^G_T_/i,
          tables: [],
        },
        {
          id: "member-master",
          name: "Mitglieder-Stamm (Member Master - M_M_)",
          icon: "mdi-account-badge-outline",
          description: "Mitgliedsklassen, Zuordnungen zu Resorts, Whitelists und Berechtigungen.",
          pattern: /^M_M_/i,
          tables: [],
        },
        {
          id: "member-contracts",
          name: "Mitglieder-Verträge (Member Contracts - M_T_)",
          icon: "mdi-file-sign",
          description: "Vertragslaufzeiten, Beitragsbuchungen, Vertragskonditionen und Verlängerungen.",
          pattern: /^M_T_/i,
          tables: [],
        },
        {
          id: "member-actions",
          name: "Zutritte & Check-Ins (Member Actions - M_A_)",
          icon: "mdi-door-open",
          description: "Turnstile-Durchgänge, Check-Ins, Sperren und Zutrittsberechtigungen.",
          pattern: /^M_A_/i,
          tables: [],
        },
        {
          id: "booking-trans",
          name: "Buchungen & Termine (Reservation - R_T_)",
          icon: "mdi-calendar-month-outline",
          description: "Gebuchte Behandlungen, Kursbelegungen, Behandlungszeiten und Wartelisten.",
          pattern: /^R_T_/i,
          tables: [],
        },
        {
          id: "booking-config",
          name: "Buchungs-Konfiguration (Res Config - R_M_ / R_A_)",
          icon: "mdi-calendar-cog-outline",
          description: "Vorlagen für Termine, Buchungsregeln, Tarife und Behandlungsarten.",
          pattern: /^(R_M_|R_A_)/i,
          tables: [],
        },
        {
          id: "billing",
          name: "Finanzen & Mahnwesen (Billing - B_T_ / B_M_ / B_A_)",
          icon: "mdi-cash-multiple",
          description: "Debitorenkonten, Mahnstufen, E-Invoicing, Rechnungsnummernkreise und Kassenbücher.",
          pattern: /^(B_T_|B_M_|B_A_)/i,
          tables: [],
        },
        {
          id: "giftcard",
          name: "Prepaid-Karten & Gutscheine (Cards - C_)",
          icon: "mdi-gift-outline",
          description: "Prepaid-Guthaben, Verifizierungscodes, Gutschein-Layouts und Saldenkontrollen.",
          pattern: /^C_/i,
          tables: [],
        },
        {
          id: "interface",
          name: "Schnittstellen & POS-Kassen (Interface - I_T_)",
          icon: "mdi-swap-horizontal-bold",
          description: "Schnittstellentransaktionen, POS-Kassenbuchungen und Hotelstay-Zahlungsmappings.",
          pattern: /^I_T_/i,
          tables: [],
        },
        {
          id: "templates",
          name: "Vorlagen & Tickets (Templates - T_)",
          icon: "mdi-ticket-percent-outline",
          description: "Ticketvorlagen, Druck-Layouts, Barcode-Zuweisungen und Service-Templates.",
          pattern: /^T_/i,
          tables: [],
        },
        {
          id: "webshop",
          name: "Webshop & Online-Buchung (Webshop - W_)",
          icon: "mdi-cart-outline",
          description: "Web-Zahlungen, Online-Gutscheinverkäufe und API-Webhooks.",
          pattern: /^W_/i,
          tables: [],
        },
        {
          id: "migration",
          name: "Migration & Imports (Imports - IMP_T_)",
          icon: "mdi-database-import-outline",
          description: "Hilfstabellen zur Migration von Altdaten, Session-Mappings und Altschlüssel.",
          pattern: /^IMP_T_/i,
          tables: [],
        },
        {
          id: "hotel-res",
          name: "Hotel & Ressourcen (Hotel/Resources - H_ / MR_)",
          icon: "mdi-office-building-marker-outline",
          description: "Hotel-Zimmerbelegung, Zimmerzuweisungen, Raum- und Therapeuten-Ressourcen.",
          pattern: /^(H_T_|MR_)/i,
          tables: [],
        },
        {
          id: "reports",
          name: "Berichte & Reports (Reports - RP_ / rp_)",
          icon: "mdi-file-chart-outline",
          description: "PDF-Reportvorlagen, Tagesabschlüsse, Umsatzstatistiken und Exportlisten.",
          pattern: /^(RP_|rp_)/i,
          tables: [],
        },
        {
          id: "views",
          name: "Datenbank-Views (Views - V_)",
          icon: "mdi-table-eye",
          description: "Vorkompilierte Views zur performanten Abfrage von Kundenhistorien.",
          pattern: /^V_/i,
          tables: [],
        },
        {
          id: "system",
          name: "System & Logbücher (System - LOG_ / Rest)",
          icon: "mdi-shield-alert-outline",
          description: "Rechteverwaltung, Audit-Logs der Query-Ausführung, temporäre Backups.",
          pattern: /.*/,
          tables: [],
        },
      ]

      // Distribute all tables from tables list
      this.tables.forEach((tableName) => {
        let matched = false
        // Do not match the last catch-all system group directly
        for (let i = 0; i < categories.length - 1; i++) {
          const cat = categories[i]
          if (cat.pattern.test(tableName)) {
            cat.tables.push(tableName)
            matched = true
            break
          }
        }
        if (!matched) {
          if (tableName.startsWith("G_")) {
            categories[0].tables.push(tableName)
          } else if (tableName.startsWith("M_")) {
            categories[3].tables.push(tableName)
          } else if (tableName.startsWith("R_")) {
            categories[6].tables.push(tableName)
          } else if (tableName.startsWith("B_")) {
            categories[8].tables.push(tableName)
          } else if (tableName.startsWith("C_")) {
            categories[9].tables.push(tableName)
          } else if (tableName.startsWith("I_")) {
            categories[10].tables.push(tableName)
          } else if (tableName.startsWith("T_")) {
            categories[11].tables.push(tableName)
          } else if (tableName.startsWith("W_")) {
            categories[12].tables.push(tableName)
          } else if (tableName.startsWith("IMP_")) {
            categories[13].tables.push(tableName)
          } else if (tableName.startsWith("H_")) {
            categories[14].tables.push(tableName)
          } else if (tableName.startsWith("RP_") || tableName.startsWith("rp_")) {
            categories[15].tables.push(tableName)
          } else if (tableName.startsWith("V_")) {
            categories[16].tables.push(tableName)
          } else {
            categories[17].tables.push(tableName)
          }
        }
      })

      return categories
    },

    // Filter categories and tables inside them based on the search query
    filteredSchemaCategories() {
      const search = (this.schemaDialog.search || "").trim().toLowerCase()
      const hideRefs = this.schemaDialog.hideReferences

      return this.schemaCategories.map((cat) => {
        const filtered = cat.tables.filter((table) => {
          // If hideReferences is enabled, hide tables matching relationship patterns
          if (hideRefs) {
            const isRef =
              /(_LINK|_MAPPING|_MAP|_TEXT|_KEY|_RELATION|_DETAIL|_WHITELIST|_PARAMETER|_IMPORT|_ADDRESS|_COMMUNICATION|_BANKDETAIL|_CREDITCARD|_COMMENT|_DOCUMENT|_FILE|_HISTORY|_LOG|_INFO|_RIGHTS|_PERMISSION|_RULE|_MIGRATION|IMP_T_)/i.test(
                table,
              )
            if (isRef) return false
          }

          // Search query matching
          if (search) {
            const tableMatch = table.toLowerCase().includes(search)
            const descMatch = this.getTableDescription(table).toLowerCase().includes(search)
            return tableMatch || descMatch
          }
          return true
        })

        return {
          ...cat,
          filteredTables: filtered,
        }
      })
    },

    // Get selected category object
    selectedSchemaCategory() {
      const idx = this.schemaDialog.activeCategoryIndex
      return this.filteredSchemaCategories[idx] || null
    },
    // Builds Meta Fields for display in deep relationship mapper details card
    lookupProfileFields() {
      if (!this.lookup.data || !this.lookup.data.profile) return []
      const p = this.lookup.data.profile
      const type = this.lookup.selectedEntity.type

      if (type === "customer") {
        return [
          { label: "Full Name", value: `${p.FIRST_NAME || ""} ${p.LAST_NAME || ""}`.trim() || "Anonymous Customer" },
          { label: "Email", value: p.EMAIL },
          { label: "Phone", value: p.PHONE || p.MOBILPHONE || "-" },
          { label: "Gender", value: this.formatGender(p.GENDER || p.GENDER_PREF) },
          { label: "Date of Birth", value: this.formatDateOnly(p.BIRTHDAY) },
          { label: "Address", value: `${p.STREET || ""} ${p.STREET2 || ""}`.trim() },
          { label: "City & Zip", value: `${p.ZIP_CODE || ""} ${p.CITY || ""}`.trim() },
          { label: "Creation Date", value: this.formatDate(p.CREATION_DATE || p.TEMPORARYCREATIONDATE) },
        ]
      } else if (type === "location") {
        return [
          { label: "Location ID (Primary Key)", value: p.LOCATIONID },
          { label: "Name", value: p.NAME || "-" },
          { label: "Short Name", value: p.SHORTNAME || "-" },
          { label: "Description", value: p.DESCRIPTION || "-" },
          { label: "Resort Group (Studio) ID", value: p.RESSORTGROUPID },
          { label: "Concurrent Checkin Limit", value: p.CONCURRENTCHECKINLIMIT || "Unlimited (0)" },
          { label: "Anonymous Guest Counting", value: p.ANONYMOUS_GUEST_COUNTING ? "Enabled" : "Disabled" },
          { label: "External Mapping ID", value: p.MAPPING || "-" },
          { label: "Status", value: p.INACTIVE ? "Inactive (Inaktiv)" : "Active (Aktiv)" },
        ]
      } else if (type === "ressortgroup") {
        return [
          { label: "Studio (Resort Group) ID", value: p.RESSORTGROUPID },
          { label: "Studio Description (Name)", value: p.DESCRIPTION || "-" },
          { label: "Print Title (Billing Name)", value: p.PRINT_DESCRIPTION || "-" },
          { label: "VAT Number (MWST-Nr.)", value: p.VATIDNUMBER || "-" },
          { label: "Address", value: p.ADDRESS1 || "-" },
          { label: "Zip & City", value: `${p.ZIPCODE || ""} ${p.CITY || ""}`.trim() || "-" },
          { label: "Email Contact", value: p.EMAIL || "-" },
          { label: "Phone", value: p.PHONE || p.MOBILEPHONE || "-" },
          { label: "Web Link", value: p.WEB || "-" },
          { label: "Bank Account Detail (County)", value: p.COUNTY || "-" },
          { label: "Sync Template ID", value: p.SYNC_TEMPLATERESSORTGROUPID || "None" },
          { label: "Status", value: p.INACTIVE ? "Inactive (Inaktiv)" : "Active (Aktiv)" },
        ]
      }
      return []
    },
    activeCustomerContracts() {
      const contracts = this.lookup.data?.contracts || []
      return contracts.filter((contract) => [1, 2, 6].includes(parseInt(contract.CONTRACTSTATE, 10)) && !contract.DELETED)
    },
    customerActivityCount() {
      if (!this.lookup.data) return 0
      return (this.lookup.data.bookings || []).length + (this.lookup.data.packages || []).length + (this.lookup.data.courses || []).length
    },
    customerCrmCount() {
      if (!this.lookup.data) return 0
      return [
        "addresses",
        "communications",
        "comments",
        "bankDetails",
        "documents",
        "segments",
        "origins",
        "opportunities",
        "correspondences",
      ].reduce((sum, key) => sum + (this.lookup.data[key] || []).length, 0)
    },
    lookupWarningSections() {
      return (this.lookup.data?.warnings || []).map((warning) => warning.section).join(", ")
    },
    customerAccountBalance() {
      return (this.lookup.data?.accounts || []).reduce((sum, account) => sum + Number(account.BALANCE || 0), 0)
    },
    nextCustomerBooking() {
      const now = Date.now()
      return (
        (this.lookup.data?.bookings || [])
          .filter((booking) => !booking.STORNO && new Date(booking.STARTDATE).getTime() >= now)
          .sort((a, b) => new Date(a.STARTDATE) - new Date(b.STARTDATE))[0] || null
      )
    },
    latestCustomerCheckin() {
      return (this.lookup.data?.checkins || []).slice().sort((a, b) => new Date(b.CHECKINDATE || 0) - new Date(a.CHECKINDATE || 0))[0] || null
    },
    nextContractEnd() {
      const endDates = this.activeCustomerContracts
        .map((contract) => contract.USE_ENDDATE || contract.ENDDATE)
        .filter(Boolean)
        .sort((a, b) => new Date(a) - new Date(b))
      return endDates.length ? `bis ${this.formatDateOnly(endDates[0])}` : "ohne Enddatum"
    },
    latestCheckinDate() {
      return this.latestCustomerCheckin ? this.formatDate(this.latestCustomerCheckin.CHECKINDATE) : "Noch kein Besuch"
    },
    latestCheckinLocation() {
      if (!this.latestCustomerCheckin) return "Keine Check-ins in den verknüpften Tabellen"
      return `Standort ${this.latestCustomerCheckin.LOCATIONID || "unbekannt"} · ${this.latestCustomerCheckin._sourceTable || "Check-in"}`
    },
    nextBookingDate() {
      return this.nextCustomerBooking ? this.formatDate(this.nextCustomerBooking.STARTDATE) : "Kein offener Termin"
    },
    nextBookingName() {
      if (!this.nextCustomerBooking) return "Keine zukünftige, aktive Buchung gefunden"
      return this.nextCustomerBooking.TEMPLATE_NAME || this.nextCustomerBooking.COURSE_NAME || `Buchung ${this.nextCustomerBooking.BOOKINGID}`
    },
    customerHealth() {
      const profile = this.lookup.data?.profile
      if (profile?.DELETED) return { label: "Profil gelöscht", color: "red accent-2" }
      if (this.activeCustomerContracts.length > 0) return { label: "Aktive Mitgliedschaft", color: "green accent-3" }
      if ((this.lookup.data?.contracts || []).length > 0) return { label: "Keine aktive Mitgliedschaft", color: "orange accent-2" }
      return { label: "Nur Gastprofil", color: "blue-grey lighten-2" }
    },
    customerOverviewStats() {
      if (!this.lookup.data) return []
      return [
        {
          label: "Aktive Verträge",
          value: this.activeCustomerContracts.length,
          hint: `${this.lookup.data.contracts.length} Verträge insgesamt`,
          icon: "mdi-file-sign",
          color: "#fbc02d",
        },
        {
          label: "Kontosaldo",
          value: `CHF ${this.formatMoney(this.customerAccountBalance)}`,
          hint: `${this.lookup.data.accounts.length} verknüpfte Konten`,
          icon: "mdi-wallet-outline",
          color: this.customerAccountBalance < 0 ? "#ff7d87" : "#64d98b",
        },
        {
          label: "Zutritt",
          value: (this.lookup.data.accessPasses || []).length,
          hint: `${(this.lookup.data.memberMedia || []).length} Medien · ${(this.lookup.data.denials || []).length} Abweisungen`,
          icon: "mdi-door-open",
          color: "#7bb8ff",
        },
        {
          label: "Aktivitäten",
          value: this.customerActivityCount,
          hint: `${this.lookup.data.checkins.length} eindeutige Besuche`,
          icon: "mdi-calendar-multiselect",
          color: "#d7a8ff",
        },
      ]
    },
  },
  watch: {
    // Watch options to trigger server-side pagination & sorting
    options: {
      handler() {
        this.fetchTableData()
      },
      deep: true,
    },
    // Watch selected table to reset page and reload columns metadata
    selectedTable() {
      this.options.page = 1
      this.fetchTableData()
    },
    // Debounce search query to prevent constant API requests while typing
    searchQuery(newVal) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1
        this.fetchTableData()
      }, 500)
    },
  },
  mounted() {
    this.fetchTablesList()
  },
  methods: {
    // Open Schema Reference Guide dialog
    openSchemaGuide() {
      this.schemaDialog.search = ""
      this.schemaDialog.activeCategoryIndex = 0
      this.schemaDialog.show = true
    },

    // Load columns on expansion
    handleTableExpand(tableName) {
      if (!tableName) return
      this.loadTableColumns(tableName)
    },

    // Load columns for category table
    loadTableColumns(tableName) {
      if (this.tableColumnsCache[tableName]) return

      this.$set(this.tableColumnsLoading, tableName, true)
      api.tacDBApi
        .getTableData({ table: tableName, limit: 1 })
        .then((res) => {
          if (res && res.success) {
            this.$set(this.tableColumnsCache, tableName, res.columns || [])
          }
        })
        .catch((err) => {
          console.error(`Failed to load column metadata for '${tableName}':`, err)
        })
        .finally(() => {
          this.$set(this.tableColumnsLoading, tableName, false)
        })
    },

    // Load Table into Main Explorer
    loadTableIntoExplorer(tableName) {
      this.selectedTable = tableName
      this.schemaDialog.show = false
    },

    // Retrieve descriptive text for any database table
    getTableDescription(tableName) {
      const customDescriptions = {
        V_CUSTOMER: "Hauptansicht für Kundendaten. Enthält Adressdaten, Kontaktdaten, Geburtsdatum, Mitgliedsnummer, E-Mail und Status.",
        V_CONTRACT_DETAILS:
          "Detaillierte Übersicht aller Verträge. Enthält Container-IDs, Laufzeiten, Raten, Whitelist-Einträge und Ressort-Gruppen.",
        V_CHECKINS: "Protokoll aller Check-Ins. Enthält Check-In-Datum, Checkout-Datum, Ausweis-Medien, Standort-IDs und Drehkreuz-Logins.",
        V_BILLS:
          "Kassenberichte und Rechnungsverzeichnisse. Enthält Gesamtbeträge, Buchungsdaten, Kassa-IDs und ressortgruppenspezifische Metadaten.",
        M_T_ACCOUNTS: "Master-Tabelle der Mitgliederkonten. Enthält Kontostand (Saldo), Fälligkeiten, Kreditlimiten und Mahnstufen.",
        V_M_ACCOUNTTRANSACTIONS: "Buchungs-Journal. Enthält chronologische Belege über Einzahlungen, Lastschriften, Abbuchungen und Buchungstexte.",
        G_M_CUSTOMERS: "Rohdaten-Stamm der Gäste und Mitglieder. Speichert Geschlecht, Kontaktdaten, Adress-IDs und Systemsperren.",
        G_A_CUSTOMER_ADDRESSES: "Zusätzliche und typisierte Profiladressen. Verknüpft über CUSTOMERID und ADDRESSTYPEID.",
        G_A_CUSTOMER_COMMUNICATIONS: "Weitere Kommunikationswege eines Profils, etwa alternative Telefonnummern oder E-Mail-Adressen.",
        V_CUSTOMERSEGMENTCODE: "Aufgelöste Profilsegmente inklusive Segmenttyp, Anzeigename, Code und Farbe.",
        V_CUSTOMERORIGIN: "Aufgelöste Herkunfts- und Aufenthaltsinformationen eines Profils.",
        M_T_CONTRACTS: "Rohdaten-Vertragssätze. Enthält Kündigungsfristen, Mindestlaufzeiten, Erstvertrag-Daten und Container-Zuweisungen.",
        M_T_ACCESSPASSES: "Kopfdatensätze für Zutrittspässe eines Kunden. Details und Gültigkeiten liegen in M_T_ACCESSPASSDETAILS.",
        M_T_ACCESSPASSDETAILS: "Gültigkeitszeiträume, Kontingente, Preise und Vorlagen-Zuordnung eines Zutrittspasses.",
        M_A_CONTRACTCUSTOMER_MEDIA: "Medien und Transponder eines Vertragskunden, verknüpft über CONTRACT_CUSTOMERID.",
        M_T_DENIALS: "Protokoll abgewiesener Zutrittsversuche mit Standort, Gerät, Vertrag und Grundtyp.",
        C_T_PREPAIDCARDS: "Guthaben-Karten (Prepaid). Speichert Ladedatum, Saldo, Status, Verifizierungscodes und Kartenmedien.",
        R_T_BOOKINGS: "Reservierungen und Termine. Verwaltet Behandlungszeiten, Ressourcen, Kursbelegungen und Therapeuten-Zuweisungen.",
        R_T_PACKAGE_CUSTOMER: "Konkrete, einem Kunden zugeordnete Pakete mit Aufenthalt, Zahlungsstatus und Rechnungsbezug.",
        R_T_COURSE_CUSTOMERS: "Teilnehmer-Zuordnung zu Kursen inklusive Preis, Rabatt, Zahlungsstatus und Rechnungsbezug.",
        G_T_OPPORTUNITIES: "CRM-Verkaufschancen eines Profils mit Typ, Status, Wahrscheinlichkeit und Studio-Zuordnung.",
        G_T_CORRESPONDENCES: "Aufgaben und Korrespondenzen eines Profils mit Fristen, Status, Zusammenfassung und Verantwortlichkeit.",
      }

      if (customDescriptions[tableName]) {
        return customDescriptions[tableName]
      }

      const lower = tableName.toLowerCase()
      if (lower.startsWith("imp_")) {
        return "Import- und Hilfstabelle zur Migration von Altdaten aus Altsystemen (SOAP-Mappings)."
      }
      if (lower.includes("_archive")) {
        return "Historische Archivtabelle. Speichert Altdaten zur Entlastung des Docker-Containers und Erhöhung der Performance."
      }
      if (lower.startsWith("log_")) {
        return "System-Logtabelle. Protokolliert Ausführungszeiten, API-Status, Backup-Aufrufe und Backend-Fehler."
      }
      if (lower.startsWith("b_t_")) {
        return "Transaktions- und Buchungsdaten der Finanzbuchhaltung (Debitoren/Mahnwesen)."
      }
      if (lower.startsWith("g_a_")) {
        return "Gäste-Metadaten und Adresszuweisungen des Profil-Moduls."
      }
      if (lower.startsWith("m_t_")) {
        return "Mitgliederdaten, Zutrittskontrollen und Vertragsbedingungen des Membership-Moduls."
      }
      if (lower.startsWith("r_t_")) {
        return "Buchungsdaten, Wartelisten und Kursbelegungen des Reservierungs-Moduls."
      }
      if (lower.startsWith("i_t_")) {
        return "Schnittstellen-Daten und Kassenbuchungen externer POS-Systeme."
      }
      return `Datenbestand der TAC-Legacy-Datenbank zur Verwaltung von '${tableName}'.`
    },

    // Fetches the whitelisted tables list for dropdown population
    fetchTablesList() {
      this.tablesLoading = true
      api.tacDBApi
        .getTables()
        .then((res) => {
          if (res && res.success) {
            this.tables = res.tables
          } else {
            throw new Error("Invalid response format for tables list.")
          }
        })
        .catch((err) => {
          console.error("Error fetching tables list:", err)
          this.errorAlert = "Failed to load database tables list."
        })
        .finally(() => {
          this.tablesLoading = false
        })
    },

    // Fetches explored table records based on selection, search, pagination, and sorting parameters
    fetchTableData() {
      this.loading = true
      this.errorAlert = null

      const limit = this.options.itemsPerPage || 10
      const offset = ((this.options.page || 1) - 1) * limit
      const sortBy = this.options.sortBy && this.options.sortBy[0] ? this.options.sortBy[0] : ""
      const sortDesc = this.options.sortDesc && this.options.sortDesc[0] ? this.options.sortDesc[0] : false

      const params = {
        table: this.selectedTable,
        limit,
        offset,
        sortBy,
        sortDesc,
        search: this.searchQuery || "",
      }

      api.tacDBApi
        .getTableData(params)
        .then((res) => {
          if (res && res.success) {
            this.columns = res.columns || []
            this.rows = res.rows || []
            this.totalRows = res.total || 0
          } else {
            throw new Error("Invalid explore API response.")
          }
        })
        .catch((err) => {
          console.error("Error exploring table data:", err)
          this.errorAlert = `Failed to fetch data for table '${this.selectedTable}'. Error: ${err.message}`
          this.rows = []
          this.columns = []
          this.totalRows = 0
        })
        .finally(() => {
          this.loading = false
        })
    },

    // Refresh control trigger
    refreshData() {
      this.fetchTableData()
    },

    // Row click handler - opens the customer dialog if CUSTOMERID column is present
    handleRowClick(item) {
      const customerId = this.getCustomerId(item)
      if (customerId !== null) {
        this.openCustomerDetails(customerId)
      }
    },

    // Fetch and open the customer profile details dialog
    openCustomerDetails(customerId) {
      this.detailDialog.customerId = customerId
      this.detailDialog.loading = true
      this.detailDialog.show = true
      this.detailDialog.activeTab = 0
      this.detailDialog.data = null

      api.tacDBApi
        .getCustomerDetails(customerId)
        .then((res) => {
          if (res && res.success) {
            this.detailDialog.data = res
          } else {
            throw new Error("Failed to load customer profile details.")
          }
        })
        .catch((err) => {
          console.error("Error fetching customer details:", err)
          this.errorAlert = `Failed to load details for customer ID ${customerId}.`
          this.detailDialog.show = false
        })
        .finally(() => {
          this.detailDialog.loading = false
        })
    },

    // Check if customer ID is present on selected record
    hasCustomerId(item) {
      return this.getCustomerId(item) !== null
    },

    // Safely reads customer ID value from record keys (case-insensitive)
    getCustomerId(item) {
      if (!item) return null
      const key = Object.keys(item).find((k) => k.toUpperCase() === "CUSTOMERID")
      if (key && item[key] !== null && item[key] !== undefined) {
        return parseInt(item[key], 10)
      }
      return null
    },

    // Truncates long strings to preserve dynamic table layouts
    shouldShowTooltip(val) {
      return val !== null && val !== undefined && val.toString().length > 35
    },

    // Dynamic formatting for columns types
    formatValue(val, type) {
      if (val === null || val === undefined) return "-"

      const lowerType = (type || "").toLowerCase()
      if (lowerType === "bit") {
        return val ? "Yes" : "No"
      }
      if (lowerType === "date" || lowerType === "datetime2" || lowerType === "datetime") {
        return this.formatDate(val)
      }
      if (typeof val === "boolean") {
        return val ? "Yes" : "No"
      }
      return val
    },

    // Generates initials for avatar display
    getInitials() {
      const p = this.detailDialog.data?.profile
      if (!p) return "C"
      const first = (p.FIRST_NAME || p.first_name || "").charAt(0)
      const last = (p.LAST_NAME || p.last_name || "").charAt(0)
      return (first + last).toUpperCase() || "C"
    },

    // Resolves full name from profile record keys (case-insensitive)
    getCustomerFullName() {
      const p = this.detailDialog.data?.profile
      if (!p) return "Loading Profile..."
      const first = p.FIRST_NAME || p.first_name || ""
      const last = p.LAST_NAME || p.last_name || ""
      return `${first} ${last}`.trim() || "Anonymous Customer"
    },

    // Formats gender strings to user-friendly text
    formatGender(gender) {
      if (!gender) return "-"
      const g = gender.toString().toLowerCase()
      if (g === "m" || g === "1") return "Male (Männlich)"
      if (g === "f" || g === "w" || g === "2") return "Female (Weiblich)"
      return gender
    },

    // Format dates cleanly with Swiss local format
    formatDate(val) {
      if (!val) return "-"
      try {
        const date = new Date(val)
        if (isNaN(date.getTime())) return val
        return date.toLocaleDateString("de-CH", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      } catch (e) {
        return val
      }
    },

    // Format dates only (e.g. Birthdays)
    formatDateOnly(val) {
      if (!val) return "-"
      try {
        const date = new Date(val)
        if (isNaN(date.getTime())) return val
        return date.toLocaleDateString("de-CH", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      } catch (e) {
        return val
      }
    },

    formatMoney(value) {
      const number = Number(value || 0)
      if (!Number.isFinite(number)) return "0.00"
      return number.toLocaleString("de-CH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    getBillPayments(billId) {
      return (this.lookup.data?.billPayments || []).filter((payment) => String(payment.BILLID) === String(billId))
    },

    getAccessPassValidity(accessPass) {
      if (accessPass.LOCKREASONID || accessPass.LOCKREASONTEXT) {
        return { label: "Gesperrt", color: "red accent-2" }
      }

      const now = Date.now()
      const from = accessPass.VALID_FROM ? new Date(accessPass.VALID_FROM).getTime() : null
      const until = accessPass.VALID_UNTIL ? new Date(accessPass.VALID_UNTIL).getTime() : null
      if (until && until < now) return { label: "Abgelaufen", color: "blue-grey lighten-1" }
      if (from && from > now) return { label: "Zukünftig", color: "cyan accent-2" }
      if (from || until) return { label: "Im Zeitraum", color: "green accent-3" }
      return {
        label: `Status ${accessPass.USAGE_STATE !== null && accessPass.USAGE_STATE !== undefined ? accessPass.USAGE_STATE : "?"}`,
        color: "grey lighten-1",
      }
    },

    maskMediumId(value) {
      if (value === null || value === undefined || value === "") return "Keine Medien-ID"
      const text = String(value)
      if (text.length <= 4) return text
      return `•••• ${text.slice(-4)}`
    },

    // Get color states for contracts
    getContractStateColor(state) {
      switch (parseInt(state, 10)) {
        case 0: // Provisory / Draft
          return "blue-grey lighten-1"
        case 1: // Active
          return "green accent-3"
        case 2: // Future / Active soon
          return "cyan accent-2"
        case 3: // Expired
          return "blue-grey darken-2"
        case 4: // Terminated by member
          return "orange accent-2"
        case 5: // Terminated by club
          return "orange accent-2"
        case 6: // Active, but in idle time
          return "purple accent-2"
        case 7: // Locked
          return "red accent-2"
        case 9: // Deleted
          return "red accent-2"
        default:
          return "grey lighten-1"
      }
    },

    // Get text representations for contract states
    getContractStateText(state) {
      switch (parseInt(state, 10)) {
        case 0:
          return "Provisory (Entwurf / Vorvertrag)"
        case 1:
          return "Active (Aktiv)"
        case 2:
          return "Future (Zukünftig)"
        case 3:
          return "Expired (Abgelaufen)"
        case 4:
          return "Terminated by member (Kündigung Mitglied)"
        case 5:
          return "Terminated by studio (Kündigung Studio)"
        case 6:
          return "Active in idle time (Aktiv / sistiert)"
        case 7:
          return "Locked (Gesperrt)"
        case 9:
          return "Deleted (Gelöscht)"
        default:
          return `State ${state || "Unknown"}`
      }
    },

    // Lookup: Type Toggle Handler
    onLookupTypeChange() {
      this.lookup.searchQuery = ""
      this.lookup.searchResults = []
      this.lookup.selectedEntity = null
      this.lookup.data = null
      this.searchLookupEntities()
    },

    // Lookup: Search debouncer
    debounceLookupSearch() {
      if (this.lookupSearchTimeout) clearTimeout(this.lookupSearchTimeout)
      this.lookupSearchTimeout = setTimeout(() => {
        this.searchLookupEntities()
      }, 400)
    },

    // Lookup: Trigger API Search
    searchLookupEntities() {
      this.lookup.searchLoading = true
      api.tacDBApi
        .searchEntities({
          search: this.lookup.searchQuery || "",
          type: this.lookup.entityType,
        })
        .then((res) => {
          if (res && res.success) {
            this.lookup.searchResults = res.results || []
          }
        })
        .catch((err) => {
          console.error("Error searching lookup entities:", err)
        })
        .finally(() => {
          this.lookup.searchLoading = false
        })
    },

    // Lookup: Select Entity
    selectLookupEntity(item) {
      this.lookup.selectedEntity = item
      this.lookup.activeTab = 0
      this.lookup.data = null
      this.fetchLookupEntityDetails(item.type, item.id)
    },

    // Lookup: Fetch mapped records from tables
    fetchLookupEntityDetails(type, id) {
      this.lookup.loading = true
      api.tacDBApi
        .getEntityDetails({ type, id })
        .then((res) => {
          if (res && res.success) {
            this.lookup.data = res
          }
        })
        .catch((err) => {
          console.error("Error loading lookup details:", err)
          this.errorAlert = `Failed to load details for ${type} ID ${id}.`
        })
        .finally(() => {
          this.lookup.loading = false
        })
    },

    async downloadPosBookingExamples() {
      if (this.posBookingExamplesDownloading || this.posMasterDataDownloading || this.posSchemaDownloading) return
      this.posBookingExamplesDownloading = true
      this.errorAlert = null
      try {
        const report = await api.tacDBApi.getPosBookingExamples()
        const validSection = (section) => section && Array.isArray(section.rows)
          && Number.isInteger(section.rowCount) && section.rowCount === section.rows.length
          && Number.isInteger(section.limit) && section.limit > 0 && section.rowCount <= section.limit
          && ["ok", "failed"].includes(section.status)
        if (report?.format !== "tac-pos-booking-examples-v1" || report.readOnly !== true
          || !["complete", "partial"].includes(report.status)
          || !Array.isArray(report.cases) || report.cases.length !== 7
          || !report.cases.every((item) => item && ["candidates_found", "not_found", "failed"].includes(item.status))
          || !Array.isArray(report.sections) || !report.sections.length || !report.sections.every(validSection)
          || !Array.isArray(report.warnings)
          || (report.status === "complete" && (report.warnings.length > 0
            || report.cases.some((item) => item.status === "failed")
            || report.sections.some((section) => section.status !== "ok"
              || (!section.sample && (section.truncated || section.expectedRowCount !== section.rowCount)))))) {
          throw Object.assign(new Error("Invalid booking-example report"), { code: "TAC_POS_EXAMPLES_INVALID_RESPONSE" })
        }
        saveAs(new Blob([JSON.stringify(report)], { type: "application/json;charset=utf-8" }),
          `tac-kassenbeispiele-${new Date().toISOString().slice(0, 10)}.json`)
        if (report.status === "partial") {
          this.errorAlert = "Die Kassenbeispiele wurden heruntergeladen. Einzelne Abfragen oder Belegdetails sind unvollständig; die Datei enthält die Warnungen."
        } else if (report.cases.every((item) => item.status === "not_found")) {
          this.errorAlert = "Die Datei wurde heruntergeladen. Für die festgelegten Suchkriterien wurden keine Belegbeispiele gefunden."
        }
      } catch (error) {
        const status = error.response?.status
        this.errorAlert = error.code === "TAC_POS_EXAMPLES_INVALID_RESPONSE"
          ? "Die Antwort enthält keinen gültigen Kassenbeispiel-Export. Bitte prüfen, ob die neue Backend-Version läuft."
          : status === 401 || status === 403
          ? "Für den Export ist eine gültige Anmeldung mit Transaktionsrecht erforderlich."
          : status === 404
          ? "Die Kassenbeispiel-Route ist auf diesem Backend noch nicht verfügbar. Bitte die Backend-Version prüfen."
          : status === 502
          ? "Die Kassenbeispiele konnten aus der Datenbank auf dem AI-Server nicht geladen werden."
          : error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" || status === 504
          ? "Der Belegexport hat zu lange gedauert. Bitte erneut versuchen."
          : "Die Kassenbeispiele konnten nicht exportiert werden."
      } finally {
        this.posBookingExamplesDownloading = false
      }
    },

    async downloadPosMasterData() {
      if (this.posMasterDataDownloading || this.posSchemaDownloading || this.posBookingExamplesDownloading) return
      this.posMasterDataDownloading = true
      this.errorAlert = null
      try {
        const report = await api.tacDBApi.getPosMasterData()
        if (report?.format !== "tac-pos-master-data-v2" || !["complete", "partial"].includes(report.status)
          || !Array.isArray(report.sections) || !report.sections.length || !Array.isArray(report.warnings)
          || (report.status === "complete" && (report.rowCountsVerified !== true || report.warnings.length > 0
            || !report.sections.every((section) => section?.status === "ok" && section.countMatches === true
              && Number.isSafeInteger(section.expectedRowCount) && section.expectedRowCount >= 0
              && section.rowCount === section.expectedRowCount && Array.isArray(section.rows)
              && section.rows.length === section.rowCount)))) {
          const responseError = new Error("Invalid master-data report")
          responseError.code = "TAC_POS_MASTER_DATA_INVALID_RESPONSE"
          throw responseError
        }
        const blob = new Blob([JSON.stringify(report)], { type: "application/json;charset=utf-8" })
        saveAs(blob, `tac-kassenstammdaten-${new Date().toISOString().slice(0, 10)}.json`)
        if (report.status === "partial") {
          this.errorAlert = "Der Stammdaten-Export wurde heruntergeladen, ist aber nicht vollständig bestätigt. Die Datei enthält fehlende Bereiche oder abweichende Datensatzanzahlen als Warnungen."
        }
      } catch (error) {
        const status = error.response?.status
        this.errorAlert = error.code === "TAC_POS_MASTER_DATA_INVALID_RESPONSE"
          ? "Die Antwort enthält keinen gültigen Kassenstammdaten-Export. Bitte prüfen, ob die neue Backend-Version läuft."
          : status === 401 || status === 403
          ? "Für den Export ist eine gültige Anmeldung mit Transaktionsrecht erforderlich."
          : status === 404
          ? "Die Kassenstammdaten-Route ist auf diesem Backend noch nicht verfügbar. Bitte prüfen, ob die neue Backend-Version läuft."
          : status === 502
          ? "Die Kassenstammdaten konnten aus der Datenbank auf dem AI-Server nicht geladen werden."
          : error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" || status === 504
          ? "Der Stammdaten-Export hat zu lange gedauert. Bitte erneut versuchen."
          : "Die Kassenstammdaten konnten nicht exportiert werden."
      } finally {
        this.posMasterDataDownloading = false
      }
    },

    async downloadPosSchema() {
      if (this.posSchemaDownloading || this.posMasterDataDownloading || this.posBookingExamplesDownloading) return
      this.posSchemaDownloading = true
      this.errorAlert = null
      try {
        const report = await api.tacDBApi.getPosSchema()
        if (report?.format !== "tac-pos-schema-v1" || !["complete", "partial"].includes(report.status)) {
          const responseError = new Error("Invalid schema report")
          responseError.code = report?.diagnostics && report.completedSections !== undefined
            ? "TAC_POS_LEGACY_RESPONSE" : "TAC_POS_INVALID_RESPONSE"
          throw responseError
        }
        const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json;charset=utf-8" })
        saveAs(blob, `tac-kassenstruktur-${new Date().toISOString().slice(0, 10)}.json`)
        if (report.status === "partial") this.errorAlert = "Der Struktur-Export ist unvollständig und enthält Warnungen."
      } catch (error) {
        const status = error.response?.status
        const responseErrors = {
          TAC_POS_LEGACY_RESPONSE: "Das Backend liefert die alte Transaktionsdiagnose statt der Kassenstruktur. Bitte prüfen, ob das angesprochene Backend die neue Version geladen hat.",
          TAC_POS_INVALID_RESPONSE: "Die Antwort enthält keinen gültigen Kassenstruktur-Export. Bitte die Backend-Version und die Export-Adresse prüfen.",
        }
        this.errorAlert = Object.prototype.hasOwnProperty.call(responseErrors, error.code) ? responseErrors[error.code]
          : status === 401 || status === 403
          ? "Für den Export ist eine gültige Anmeldung mit Transaktionsrecht erforderlich."
          : status === 404
          ? "Die Kassenstruktur-Route ist auf diesem Backend noch nicht verfügbar. Bitte prüfen, ob die neue Backend-Version läuft."
          : status === 502
          ? "Die TAC-Struktur konnte aus der Datenbank auf dem AI-Server nicht geladen werden."
          : error.code === "ECONNABORTED"
          ? "Der Kassenstruktur-Export hat zu lange gedauert. Bitte erneut versuchen."
          : "Die Kassenstruktur konnte nicht exportiert werden."
      } finally {
        this.posSchemaDownloading = false
      }
    },

    // Lookup: Export data helper (Blob CSV download)
    exportLookupTableToCSV(data, fileName) {
      if (!data || data.length === 0) return

      const headers = Object.keys(data[0]).filter((k) => !k.startsWith("_"))
      const csvRows = []
      csvRows.push(headers.join(","))

      for (const row of data) {
        const values = headers.map((header) => {
          const val = row[header]
          const escaped = ("" + (val === null || val === undefined ? "" : val)).replace(/"/g, '""')
          return `"${escaped}"`
        })
        csvRows.push(values.join(","))
      }

      const csvString = csvRows.join("\n")
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.setAttribute("href", url)
      link.setAttribute("download", `${fileName.toLowerCase()}_export.csv`)
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async downloadAllBookingsCSV() {
      try {
        this.lookup.allBookingsDownloading = true
        const blob = await api.tacDBApi.exportAllBookingsCSV()
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.setAttribute("href", url)
        link.setAttribute("download", `all_bookings_by_customer.csv`)
        link.style.visibility = "hidden"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error("Error downloading bookings CSV:", error)
        this.errorAlert = "Fehler beim Herunterladen der Termine: " + (error.message || error)
      } finally {
        this.lookup.allBookingsDownloading = false
      }
    },

    // Lookup: Get dynamic history table columns
    getHistoryTableHeaders(rows) {
      if (!rows || rows.length === 0) return []
      return Object.keys(rows[0]).filter((k) => !k.startsWith("_"))
    },

    // Lookup: Toggle expandable contract row
    toggleContractExpand(contractId) {
      this.$set(this.lookup.expandedContracts, contractId, !this.lookup.expandedContracts[contractId])
    },

    // Lookup: Filter contract keys for clean presentation in detailed expansion card
    getFilteredContractDetails(contract) {
      const details = {}
      Object.keys(contract).forEach((key) => {
        if (key.startsWith("_") || key === "CATEGORY_NAME") return
        let val = contract[key]
        if (val === null || val === undefined) {
          val = "-"
        } else if (typeof val === "boolean") {
          val = val ? "Yes" : "No"
        } else if (
          key.toLowerCase().includes("date") ||
          key.toLowerCase().includes("time") ||
          key === "RENEWAL_UNTIL" ||
          key === "SIGNATURE_VALIDUNTIL"
        ) {
          val = this.formatDate(val)
        }
        details[key] = val
      })
      return details
    },

    // Format runtime units (Tage, Wochen, Monate, Jahre)
    formatUnit(unit) {
      switch (parseInt(unit, 10)) {
        case 0:
          return "Tag(e)"
        case 1:
          return "Woche(n)"
        case 2:
          return "Monat(e)"
        case 3:
          return "Jahr(e)"
        default:
          return `Einheit ${unit}`
      }
    },

    // Get prerequisites/required categories for Zusatzpakete
    getCategoryDependencies(categoryId) {
      if (!this.lookup.data || !this.lookup.data.contractDependencies) return []
      return this.lookup.data.contractDependencies.filter((d) => d.CategoryID === categoryId)
    },

    // Get category name by ID
    getCategoryNameById(categoryId) {
      if (!this.lookup.data || !this.lookup.data.contractCategories) return `Abo ${categoryId}`
      const found = this.lookup.data.contractCategories.find((c) => c.CATEGORYID === categoryId)
      return found ? found.CATEGORY_NAME : `Abo ${categoryId}`
    },

    // Get available Zusatzpakete for a main contract
    getAvailableZusatzForMain(mainCategoryId) {
      if (!this.lookup.data || !this.lookup.data.contractDependencies) return []
      return this.lookup.data.contractDependencies.filter((d) => d.Required_CategoryID === mainCategoryId)
    },

    // Get assignment status and channel (Webshop Active, Webshop Inactive, Internal Only, Unassigned)
    getCategoryWebshopStatus(categoryId) {
      if (!this.lookup.data || !this.lookup.data.contractAssignments) return { text: "Nur Intern", color: "blue-grey lighten-1" }
      const selectedStudioId = this.lookup.selectedEntity ? parseInt(this.lookup.selectedEntity.id, 10) : null
      if (!selectedStudioId) return { text: "Unbekannt", color: "grey" }

      const assigns = this.lookup.data.contractAssignments.filter(
        (a) => a.CATEGORYID === categoryId && parseInt(a.RESSORTGROUPID, 10) === selectedStudioId,
      )

      if (assigns.length === 0) return { text: "Nicht zugeordnet", color: "red darken-3 white--text" }

      // Check if any of the assignments are webshop and active
      const webshopAssign = assigns.find((a) => a.IS_WEBSHOP === 1)
      if (webshopAssign) {
        return webshopAssign.WEBSHOP_INACTIVE
          ? { text: "Webshop Inaktiv", color: "red accent-2" }
          : { text: "Webshop Aktiv", color: "green accent-3" }
      }

      return { text: "Nur Intern", color: "blue-grey lighten-1 white--text" }
    },

    // Get contract prices and fees formatted
    getCategoryPrices(categoryId) {
      if (!this.lookup.data || !this.lookup.data.contractPrices) return []
      const prices = this.lookup.data.contractPrices.filter((p) => p.CATEGORYID === categoryId)

      const seenPayTypes = new Set()
      const filteredPrices = []

      prices.forEach((p) => {
        const key = `${p.PAYTYPE}_${p.PAYINTERVAL}`
        if (!seenPayTypes.has(key)) {
          seenPayTypes.add(key)
          filteredPrices.push(p)
        }
      })

      return filteredPrices.map((p) => {
        const formattedVal = parseFloat(p.VALUE).toFixed(2)
        let intervalText = ""
        if (parseInt(p.PAYTYPE, 10) === 1) {
          intervalText = "einmalig"
        } else {
          const intervalVal = parseInt(p.PAYINTERVAL, 10)
          if (intervalVal === 1) {
            intervalText = "monatlich"
          } else if (intervalVal === 12) {
            intervalText = "jährlich"
          } else {
            intervalText = `alle ${intervalVal} Mon.`
          }
        }
        return {
          val: formattedVal,
          desc: p.PAYMENT_DESC || "Gebühr",
          interval: intervalText,
          type: p.PAYTYPE,
        }
      })
    },
  },
}
</script>

<style scoped>
/* Scoped custom styling for well come FIT dark UI */
.tac-lookup-shell {
  background: radial-gradient(circle at 12% 0%, rgba(251, 192, 45, 0.08), transparent 28rem),
    radial-gradient(circle at 100% 12%, rgba(61, 117, 255, 0.08), transparent 34rem), #0b0c0e;
  min-height: 100vh;
  color: white;
  font-family: "Inter", sans-serif;
}

.dashboard-container {
  max-width: 1600px;
  margin: 0 auto;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -1px;
}

.dashboard-header {
  padding: 18px 0 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.header-copy {
  max-width: 820px;
}

.eyebrow,
.overview-stat-label {
  color: #8e8e93;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1.3;
  text-transform: uppercase;
}

/* Custom Cards */
.control-card,
.table-card,
.stat-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
}

.stat-card {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.table-context {
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

/* Custom Select & Search Fields */
.custom-select :deep(.v-input__control),
.custom-search :deep(.v-input__control) {
  border-radius: 8px !important;
  background-color: rgba(255, 255, 255, 0.04) !important;
}

/* Custom Table styling */
.custom-table :deep(table) {
  background-color: transparent !important;
}

.custom-table :deep(thead th) {
  font-weight: 800 !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.8px;
  color: #8e8e93 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-table :deep(tbody td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
  height: 48px;
  font-size: 13px !important;
}

.custom-table :deep(tbody tr) {
  transition: background-color 0.2s ease-in-out;
}

.custom-table :deep(tbody tr:hover) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

/* Clickable Customer Row styling */
.clickable-row {
  cursor: pointer;
}

.clickable-row:hover td {
  color: #fbc02d !important;
  background-color: rgba(251, 192, 45, 0.05) !important;
}

/* Details Dialog & Tab Styling */
.border-highlight {
  border: 1px solid rgba(251, 192, 45, 0.2) !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-tabs :deep(.v-tabs-bar) {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-radius: 8px !important;
}

.lookup-tabs :deep(.v-slide-group__content) {
  gap: 4px;
  padding: 4px;
}

.lookup-tabs :deep(.v-tab) {
  min-width: auto !important;
  padding-left: 14px !important;
  padding-right: 14px !important;
}

.custom-tabs :deep(.v-tab) {
  color: #8e8e93 !important;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.custom-tabs :deep(.v-tab--active) {
  color: #000000 !important;
  background-color: #fbc02d !important;
  border-radius: 6px !important;
}

.detail-table :deep(table) {
  background-color: transparent !important;
}

.detail-table th {
  color: #8e8e93 !important;
  font-size: 11px !important;
  font-weight: bold !important;
}

.detail-table td {
  font-size: 13px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.detail-table :deep(tbody tr:hover) {
  background: rgba(255, 255, 255, 0.025) !important;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
}

/* Tooltip and utility stylings */
.truncated-text {
  cursor: help;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
}

.tooltip-content {
  font-size: 12px;
  line-height: 1.4;
  max-width: 300px;
  word-wrap: break-word;
}

.gap-3 {
  gap: 12px;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

/* Schema Reference Guide styling */
.active-list-item {
  background-color: #fbc02d !important;
  color: #000000 !important;
}
.active-list-item .v-icon,
.active-list-item .grey--text {
  color: #000000 !important;
}

.border-panel {
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: border-color 0.2s ease-in-out;
}
.border-panel:hover {
  border-color: rgba(251, 192, 45, 0.3) !important;
}

.custom-expansion-panels :deep(.v-expansion-panel-header) {
  background-color: transparent !important;
}

.custom-expansion-panels :deep(.v-expansion-panel-content__wrap) {
  padding: 0 !important;
}

/* Deep Lookup Styles */
.custom-top-tabs :deep(.v-tabs-bar) {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 12px !important;
}

.custom-btn-toggle {
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  overflow: hidden;
}

.results-list-container {
  background-color: rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  max-height: 250px;
  overflow-y: auto;
}

.lookup-result-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.lookup-result-item:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.active-result-item {
  background-color: rgba(251, 192, 45, 0.15) !important;
  border: 1px solid rgba(251, 192, 45, 0.3) !important;
}

.overview-stat-card {
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg, rgba(34, 35, 39, 0.98), rgba(24, 25, 28, 0.98)) !important;
  border: 1px solid rgba(255, 255, 255, 0.075) !important;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2) !important;
}

.overview-stat-card::after {
  position: absolute;
  right: -24px;
  bottom: -40px;
  width: 100px;
  height: 100px;
  content: "";
  background: rgba(251, 192, 45, 0.04);
  border-radius: 50%;
}

.overview-stat-icon {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
}

.section-heading {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.overview-list-item,
.compact-record {
  display: flex;
  align-items: center;
  min-height: 58px;
  padding: 11px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
}

.overview-list-item:last-child,
.compact-record:last-child {
  border-bottom: 0;
}

.source-map-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.source-map-item {
  min-width: 0;
  padding: 11px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 9px;
}

.source-table-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-compact {
  padding: 20px 8px;
  color: #8e8e93;
  font-size: 12px;
  text-align: center;
}

.min-width-0 {
  min-width: 0;
}

@media (min-width: 960px) {
  .entity-profile-card {
    position: sticky;
    top: 18px;
  }
}

@media (max-width: 600px) {
  .tac-lookup-shell {
    padding-top: 12px !important;
  }

  .source-map-list {
    grid-template-columns: 1fr;
  }

  .section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .overview-list-item {
    align-items: flex-start;
  }
}

.max-width-p {
  max-width: 450px;
}
</style>
